import type { FeedItem, NewsFeed, StateBriefing } from "./types";

/* Researched October 29, 2026 from AP, Reuters, official data releases, state
 * election offices and state newspapers. Written to be neutral: no polls,
 * forecasts, ratings, fundraising or wording that says who is ahead. */

/** Major national developments since June 2026. */
export const NATIONAL_NEWS: string[] = [
  "Annual CPI inflation was 3.7% and unemployment 4.2% in September (BLS). The economy grew at a 3.4% annual rate in the third quarter (BEA, Oct. 29). Gas averaged $4.23 on Oct. 29, versus $3.04 a year ago (AAA). The Fed raised rates Sept. 16, then held at 3.75%-4% Oct. 28. Stocks hit records in October.",
  "A June 17 U.S.-Iran interim deal collapsed; U.S. strikes and a blockade resumed in July, and Iranian attacks on Hormuz shipping surged in October. The administration says Iran must never get nuclear weapons; Democrats pushed resolutions to end the war.",
  "In June, Republicans enacted about $70 billion for ICE and Border Patrol through 2029, saying agents need resources; Democrats opposed it, seeking body-camera and warrant requirements. A stopgap funds the government through Dec. 11, averting an Oct. 1 shutdown.",
  "New 10%-12.5% tariffs on 60 trading partners replaced expiring global tariffs July 24. The administration cited inadequate enforcement of forced-labor import bans; critics said replacing tariffs was the goal. Insurers proposed a median 15% ACA premium increase for 2027 (KFF).",
  "On June 29-30, the Supreme Court rejected Trump's birthright citizenship order, let presidents fire independent-agency members, kept Lisa Cook on the Fed for now, upheld state bans on transgender athletes in girls' and women's sports and ended party coordinated-spending limits.",
  "New House maps apply in Alabama, California, Florida, Louisiana, North Carolina, Ohio, Tennessee, Texas and Utah; Missouri's was frozen pending a referendum. Courts blocked a Trump-ordered Postal Service mail-ballot rule the administration said prevents fraud; judges said USPS lacked authority.",
  "Hurricane Isaias, the Atlantic season's first hurricane, hit the Florida Panhandle Oct. 9 as a Category 2; officials reported at least eight deaths in Florida and Alabama. NOAA said June-August was the contiguous U.S.'s hottest summer on record.",
];

/** Recent national stories per feed, as each kind of outlet ran them. */
export const NATIONAL_FEEDS: Record<NewsFeed, FeedItem[]> = {
  left: [
    {
      headline: "Trump DOJ Charges James Comey a Third Time Over Alleged Leaks",
      outlet: "The New Republic",
      summary:
        "Comey called it an 'appalling abuse' as Trump's DOJ charged him a third time over an alleged 2016 leak, just hours after a judge ruled it illegally fired his daughter.",
    },
    {
      headline:
        "Trump says he ordered DoJ to file lawsuit to block Alaska Senate results certification",
      outlet: "The Guardian",
      summary:
        "Trump's DoJ wants Alaska's Senate certification halted if the GOP incumbent loses, citing 'voter confusion' over two Dan Sullivans, an 'unprecedented intervention' experts say rests on 'absurdly thin' grounds.",
    },
    {
      headline: "Trump Thinks Repulsive Ohio Attack Ad Is 'So Good,' Actually",
      outlet: "HuffPost",
      summary:
        "Trump cheered Vivek Ramaswamy's ad in which Amy Acton's estranged mother, who stood by Acton's abusive stepfather, calls her a liar, and blasted GOP Gov. Mike DeWine for objecting.",
    },
    {
      headline:
        "Trump's raid on Rep. Ilhan Omar's home puts his authoritarian intimidation on display",
      outlet: "MS NOW",
      summary:
        "Raiding Rep. Ilhan Omar's home and seizing her passport over decades-old immigration paperwork weeks before an election shows intimidation is the point, and the administration could do it to anyone.",
    },
  ],
  right: [
    {
      headline:
        "Legacy Media Exposes How Dems Are Rigging Pivotal Senate Election",
      outlet: "The Daily Wire",
      summary:
        "Even the New York Times reports Mary Peltola's aides and Senate Democrats' campaign arm worked to recruit a second 'Dan Sullivan' to confuse Alaska voters, undercutting her campaign's repeated denials.",
    },
    {
      headline:
        "Obama's 'Saddam Hussein's Cheesecake Factory' jab at Trump comes back to bite him: 'Guy built himself a wizard prison'",
      outlet: "New York Post",
      summary:
        "Obama likened Trump's White House bling to a Saddam Hussein-run Cheesecake Factory, but critics mocked his own 'Death Star' presidential center and called the jab classic Obama snobbery.",
    },
    {
      headline:
        "Trump says Abdul el Sayed is '100%' a 'Muslim Brotherhood guy'",
      outlet: "Washington Examiner",
      summary:
        "President Trump described Michigan Democratic Senate nominee Abdul El Sayed as a 'Muslim Brotherhood guy' at a Rose Garden lunch while previewing a trip to support Republican Mike Rogers.",
    },
    {
      headline: "Ilhan Omar's DC home raided by HSI",
      outlet: "Fox News",
      summary:
        "Federal HSI agents raided Somali-born Rep. Ilhan Omar's D.C. home in a criminal fraud probe of her immigration paperwork, the latest controversy for a congresswoman long dogged by marriage-fraud allegations.",
    },
  ],
  wire: [
    {
      headline:
        "Alaska's governor orders a probe into Peltola's Senate campaign over spoiler candidate controversy",
      outlet: "Associated Press",
      summary:
        "Republican Gov. Mike Dunleavy acted after revelations that Mary Peltola's campaign appeared to be involved in recruiting a second Dan Sullivan; Trump urged prosecution, and Peltola called it a distraction.",
    },
    {
      headline:
        "Trump and Obama trade barbs in closing week of US midterm election",
      outlet: "Reuters",
      summary:
        "Obama told a Georgia rally America's values are on the ballot, while Trump, stumping for Sen. Jon Husted in Ohio amid protesters, defended his White House decor against Obama's mockery.",
    },
    {
      headline:
        "Trump administration attempting to prosecute former FBI Director James Comey again",
      outlet: "Associated Press",
      summary:
        "Newly unsealed filings show the Justice Department pursuing charges against Comey over what it calls a decade-old classified leak; his lawyers say the effort is legally invalid and too late.",
    },
    {
      headline:
        "Agents search Rep. Omar's Washington home in criminal probe as she vows she won't 'be silenced'",
      outlet: "Associated Press",
      summary:
        "Federal agents searched Democratic Rep. Ilhan Omar's home in a criminal probe weeks before the midterms; Democrats questioned the timing, Omar called it political targeting, and Trump denied involvement.",
    },
  ],
};

export const STATE_BRIEFINGS: Partial<Record<string, StateBriefing>> = {
  AK: {
    news: [
      "Ballot Measure 2 would repeal the top-four primary, ranked-choice voting and 2020 campaign disclosure rules.",
      "In an Oct. 22 suit President Trump said he ordered, the Justice Department seeks to block certification of the Senate result if votes for namesake candidate Daniel J. Sullivan Jr. could have decided it; the state seeks dismissal.",
    ],
    races: {
      governor:
        "Open seat, on a four-candidate ranked-choice ballot set by the Aug. 18 top-four primary. Democratic former state Rep. Jonathan Kreiss-Tomkins of Sitka (2013–2023), whose running mate is independent Anchorage Assembly member Zac Johnson, faces three Republicans: Bernadette Wilson, a business owner and conservative activist who has not held elected office and sponsored the initiative to repeal ranked-choice voting; former Anchorage Mayor Dave Bronson (2021–2024); and former Attorney General Treg Taylor (2021–2025). Kreiss-Tomkins placed first in the primary; Democrat Tom Begich, who placed second, withdrew and endorsed him, as did Republican U.S. Sen. Lisa Murkowski and former Republican state Sen. Lesil McGuire. Republican leaders are urging voters to rank all three Republicans. Kreiss-Tomkins backs a smaller, sustainable Permanent Fund dividend and taxing oil producer Hilcorp; the Republicans oppose new taxes, with Wilson backing the full statutory dividend.",
      senate:
        "Republican Sen. Dan Sullivan, seeking a third term, faces Democratic former U.S. Rep. Mary Peltola on a four-candidate ranked-choice ballot set by the Aug. 18 top-four primary. Peltola has emphasized living costs, reversing Medicaid cuts and banning factory trawling; Sullivan has emphasized tax cuts, fisheries bycatch legislation and federal rural health funding.",
    },
    voting:
      "Early voting began Oct. 19; mailed ballots must be postmarked by Nov. 3 and, if sent within the U.S., arrive by Nov. 13.",
  },
  AL: {
    news: [
      "On June 2 the U.S. Supreme Court let Alabama use its 2023 congressional map, prompting new Aug. 11 primaries in four House districts.",
      "Tuberville declined to debate Jones, saying their differences were clear; Jones held a solo event Oct. 21 contrasting their records.",
    ],
    races: {
      governor:
        "Open seat; Gov. Kay Ivey is term-limited. Both nominees propose ending the state grocery tax. Republican U.S. Sen. Tommy Tuberville has also emphasized job recruitment, phased income tax cuts and smaller state government; Democratic former U.S. Sen. Doug Jones has emphasized Medicaid expansion, public education and a state lottery.",
      senate:
        "Open seat; Sen. Tommy Tuberville is running for governor. Republican U.S. Rep. Barry Moore has emphasized border security, ending congressional stock trading and his ties to President Trump; Democratic attorney Everett Wess has emphasized living costs, crime and a higher federal minimum wage.",
    },
    voting:
      "Alabama has no early voting; absentee voting requires an excuse and a copy of photo ID. In-person absentee applications are due Oct. 29, hand-delivered ballots Nov. 2, and mailed ballots must arrive by noon Nov. 3.",
  },
  AR: {
    news: [
      "Cotton did not join the Oct. 12 and Oct. 22 Senate debates; a planned Cotton-Shoffner debate was canceled when host Salem Media withdrew its offer.",
      "Four statewide issues are on the ballot, including citizen-only voting and gun-rights language.",
    ],
    races: {
      governor:
        'Republican Gov. Sarah Huckabee Sanders, seeking a second term, faces Democratic state Sen. Fredrick "Fred" Love and Libertarian Colt Shelby. Sanders has emphasized tax cuts, school choice and Medicaid work requirements; Love has pledged to end school vouchers, suspend the fuel tax and halt new data centers; Shelby has criticized the LEARNS education law.',
      senate:
        "Republican Sen. Tom Cotton seeks a third term against Democrat Hallie Shoffner, a farmer and first-time candidate, and Libertarian Jeff Wadlin. Cotton has emphasized tax cuts, the military, law enforcement and border security; Shoffner has emphasized farm policy, rural hospitals, living costs and ending the Iran war.",
    },
    voting:
      "Early voting runs Oct. 19–Nov. 2. Absentee voting requires an excuse such as absence or illness; mailed absentee ballots must arrive by 7:30 p.m. Nov. 3, and hand-delivered ones by Oct. 30.",
  },
  AZ: {
    news: [
      "Biggs won the July 21 Republican primary over U.S. Rep. David Schweikert.",
      "Hobbs declined the Oct. 6 Clean Elections debate, which Biggs attended along with No Labels nominee Teri Hourihan.",
    ],
    races: {
      governor:
        "Democratic Gov. Katie Hobbs, seeking a second term with former Mesa Mayor John Giles as running mate, faces Republican U.S. Rep. Andy Biggs and running mate Sine Kerr, a former state senator; it is Arizona's first election with a lieutenant governor on the ticket. Hobbs has emphasized housing costs, water and border security, and her ads have criticized Biggs's efforts to overturn the 2020 election; Biggs has emphasized phasing out the state income tax and Medicaid fraud, and his ads have criticized a state contractor's rate increase after its executives donated to Hobbs.",
    },
    voting:
      "In-person early voting runs Oct. 7–30, with emergency voting through Nov. 2. Mailed early ballots must be received, not just postmarked, by 7 p.m. Nov. 3; voters can also leave them at drop boxes or voting sites through Election Day.",
  },
  CA: {
    news: [
      "Becerra and Hilton advanced from the June 2 top-two primary; Democrat Tom Steyer finished third.",
      "The ballot has 14 statewide propositions, including Prop. 40, a one-time 5% tax on billionaires' wealth, and Prop. 39, a voter ID requirement.",
    ],
    races: {
      governor:
        "Open seat; Gov. Gavin Newsom is term-limited. Democrat Xavier Becerra, former U.S. health secretary and state attorney general, has emphasized housing construction, down-payment aid, lower health and utility costs and opposing Trump administration policies. Republican Steve Hilton, a former Fox News host and adviser to British Prime Minister David Cameron, has emphasized ending state income tax on the first $150,000 of income, lower gas and utility costs and deregulation. They held one televised debate, on Sept. 30.",
    },
    voting:
      "Counties began mailing every active voter a ballot by Oct. 5; mailed ballots must be postmarked by Nov. 3 and received by Nov. 10, or dropped off by 8 p.m. Election Day. Vote centers opened Oct. 24 in Voter's Choice Act counties, and same-day conditional registration is available.",
  },
  CO: {
    news: [
      "In the June 30 primaries, Weiser defeated U.S. Sen. Michael Bennet and Marx narrowly defeated state Sen. Barbara Kirkmeyer.",
      "The ballot has 14 statewide measures, including competing proposals for a graduated income tax and a 4.4% income tax cap.",
    ],
    races: {
      governor:
        "Open seat; Gov. Jared Polis is term-limited. Democratic Attorney General Phil Weiser has emphasized school funding, housing partnerships and environmental enforcement; Republican Victor Marx, a Marine Corps veteran and ministry founder, has emphasized auditing state spending including Medicaid, cutting regulations and energy reliability.",
      senate:
        "Democratic Sen. John Hickenlooper, who says this would be his last term, faces Republican state Sen. Mark Baisley. Hickenlooper has blamed tariffs, the 2025 Republican tax law and the Iran war for rising costs and wants the wealthy to pay more; Baisley has called for smaller government, defended the tariffs and favors a free-market approach to health care.",
    },
    voting:
      "Colorado votes by mail: ballots went out Oct. 2–9 and must be received, not postmarked, by 7 p.m. Nov. 3. Voter centers opened Oct. 19 for in-person voting and same-day registration.",
  },
  CT: {
    news: [
      "Lamont defeated challenger Josh Elliott in the Aug. 11 Democratic primary, the first primary challenge to a sitting Connecticut governor in 48 years; Fazio was unopposed.",
    ],
    races: {
      governor:
        "Democratic Gov. Ned Lamont is seeking a third term against Republican state Sen. Ryan Fazio of Greenwich. Lamont has emphasized budget surpluses, middle-class income tax cuts and lower electric bills; Fazio has emphasized property tax relief, restoring budget guardrails and taxing large university endowments. In debates Oct. 22 and Oct. 27 they also differed on limiting state cooperation with federal immigration enforcement.",
    },
    voting:
      "In-person early voting runs Oct. 19–Nov. 1. Absentee voting no longer requires an excuse; mailed or drop-box ballots must arrive by 8 p.m. Nov. 3. Same-day registration is available on Election Day.",
  },
  DE: {
    news: [
      "Coons and Katz won their Sept. 15 primaries; Katz's primary opponent, John Shulli, is running a write-in campaign.",
    ],
    races: {
      senate:
        'Democratic Sen. Chris Coons, in office since 2010, faces Republican Michael Katz, a physician and former Democratic state senator. Coons has emphasized his experience and bipartisanship, criticized tariffs and defended the Affordable Care Act; Katz has emphasized affordability, a "clean slate" overhaul of health care and balanced budgets. They met at an Oct. 22 candidate forum.',
    },
    voting:
      "Early voting runs Oct. 22–Nov. 1, except Oct. 25. Absentee voting requires a qualifying reason, and absentee ballots must reach county election offices by 8 p.m. Nov. 3.",
  },
  FL: {
    news: [
      "Hurricane Isaias made landfall near Destin on Oct. 9 as a Category 2 storm, causing widespread damage in the western Panhandle.",
      "A congressional map passed by the Republican-led Legislature in April is in use after the state Supreme Court declined June 10 to block it.",
    ],
    races: {
      governor:
        "Open seat. Republican U.S. Rep. Byron Donalds has pledged to build on Gov. Ron DeSantis' policies and backs Amendment 3, a larger homestead property-tax exemption. Democrat David Jolly, a former Republican congressman, has emphasized housing, health care, education and a state-run hurricane insurance fund, opposes Amendment 3 and declined to debate.",
      senate:
        "Special election to finish Marco Rubio's term. Republican Sen. Ashley Moody, a former state attorney general appointed in 2025, has emphasized backing Trump's agenda and law enforcement, and declined debate invitations. Democratic state Rep. Angie Nixon, who defeated Alex Vindman in the Aug. 18 primary, has emphasized Medicare for All, free child care and a rent freeze.",
    },
    voting:
      "Early voting runs Oct. 24–31 statewide and Oct. 19–Nov. 1 in many large counties. Mail ballots must reach county election offices by 7 p.m. Nov. 3.",
  },
  GA: {
    news: [
      "In June 16 Republican runoffs, Jackson defeated Trump-endorsed Lt. Gov. Burt Jones for governor, and Trump-endorsed Collins defeated Kemp-backed Derek Dooley for Senate.",
      "Three constitutional amendments are on the ballot: a larger farmland tax break, nonpartisan probate judge elections and a 911 fund.",
    ],
    races: {
      governor:
        "Open seat. Democrat Keisha Lance Bottoms, a former Atlanta mayor, has emphasized repealing the state grocery tax, expanding Medicaid and pausing new data centers. Republican Rick Jackson, founder of Jackson Healthcare, has emphasized halving the state income tax, ending state tax on tips and freezing home valuations. They debated once.",
      senate:
        "Democratic Sen. Jon Ossoff seeks a second term against Republican U.S. Rep. Mike Collins. Ossoff has emphasized capping insulin and prescription costs, cracking down on corporate homebuying and banning congressional stock trading. Collins has emphasized support for Trump's agenda, immigration enforcement and the Laken Riley Act he sponsored. They debated twice.",
    },
    voting:
      "Early in-person voting runs Oct. 13–30. Absentee ballots must arrive by 7 p.m. Nov. 3. A Dec. 1 runoff is held if no candidate wins a majority.",
  },
  HI: {
    news: [
      "In the Aug. 8 primary, U.S. Rep. Ed Case won the 1st Congressional District Democratic nomination over Jarrett Keohokalole.",
      "Two constitutional amendments are on the ballot: more time for state Senate confirmation of judges, and county infrastructure bonds repaid by property-tax growth.",
    ],
    races: {
      governor:
        "Democratic Gov. Josh Green, a physician seeking a second term with running mate Derek Kawakami, has emphasized building affordable housing, expanding kauhale homeless villages, tax relief and lowering electricity bills. Republican Gary Cordery, a general contractor running with Daniel Anthony, has emphasized the cost of living, faster building permits and government transparency. No debates have been held.",
    },
    voting:
      "Hawaii votes by mail; ballots must be received by 7 p.m. Nov. 3, and postmarks do not count. Voter service centers offer in-person voting and same-day registration Oct. 20–Nov. 3, except Sundays.",
  },
  IA: {
    news: [
      "In the June 2 primaries, Lahn defeated Trump-endorsed U.S. Rep. Randy Feenstra, Turek defeated state Sen. Zach Wahls and Hinson defeated Jim Carlin.",
      "Amendment 1 would require two-thirds legislative votes to raise income tax rates.",
    ],
    races: {
      governor:
        "Open seat; Gov. Kim Reynolds is not running. Democrat Rob Sand, the two-term state auditor and the only Democrat holding statewide office in Iowa, ran unopposed in the June primary and has emphasized a bipartisan approach, his rural upbringing, water quality and wetlands restoration. Republican Zach Lahn, a businessman who won the June primary in an upset over U.S. Rep. Randy Feenstra and is endorsed by President Trump, Vice President JD Vance, Gov. Reynolds and MAHA Action, has emphasized cancer rates, family farms and eventually ending property taxes on primary homes. They debated three times.",
      senate:
        "Open seat; Sen. Joni Ernst is retiring. Republican U.S. Rep. Ashley Hinson, a former TV news anchor endorsed by Trump, has emphasized border security, temporary gas-tax relief and opposing Social Security cuts. Democratic state Rep. Josh Turek of Council Bluffs, a two-time Paralympic gold medalist in wheelchair basketball, has emphasized ending the Iran war, suspending gas taxes and his bipartisan record. They debated Oct. 7 and Oct. 13.",
    },
    voting:
      "Early in-person voting at county auditor offices runs Oct. 14–Nov. 2; mail ballots must reach the auditor by 8 p.m. Nov. 3. Voters may register at polling places on Election Day.",
  },
  ID: {
    news: [
      "Proposition 1 would create a right to abortion before fetal viability; amendments making English the official language and reserving drug legalization to the Legislature are also on the ballot.",
    ],
    races: {
      governor:
        "Republican Gov. Brad Little, endorsed by Trump, seeks a third term, emphasizing tax cuts and education and workforce funding. Democrat Terri Pickens, a trial lawyer, has pledged to reverse 2025 tax cuts to restore Medicaid and school funding. Independent John Stegner, a retired state Supreme Court justice, has pledged to challenge the private-school tax credit in court.",
      senate:
        "Republican Sen. Jim Risch, chairman of the Foreign Relations Committee, seeks a fourth term with Trump's endorsement, emphasizing his conservative record and cutting federal waste. Independent Todd Achilles, an Army veteran and former Democratic state representative, has emphasized term limits, a congressional stock-trading ban and the national debt. No Democrat is on the ballot after nominee David Roth withdrew.",
    },
    voting:
      "Early voting runs Oct. 13–30 on county-set schedules; absentee ballots must arrive by 8 p.m. Nov. 3. Voters may register at polling places on Election Day.",
  },
  IL: {
    news: [
      "Pritzker has said lawmakers could act on Chicago Bears stadium legislation during the Nov. 17–19 veto session.",
    ],
    races: {
      governor:
        "Democratic Gov. JB Pritzker seeks a third term in a rematch with former state Sen. Darren Bailey. Pritzker has emphasized ending the state grocery tax, doubling the child tax credit and opposing Trump's agenda. Bailey has emphasized repealing the SAFE-T Act, which ended cash bail, capping property tax bills and auditing state spending. They met in one forum, on Oct. 13.",
      senate:
        "Open seat; Sen. Dick Durbin is retiring. Democratic Lt. Gov. Juliana Stratton has emphasized a $25 federal minimum wage, Medicare for All, abolishing ICE and opposing Trump's policies. Republican Don Tracy, a former state party chairman, has emphasized lowering energy costs through domestic production, immigration enforcement and tax cuts. They debated once, on Oct. 20.",
    },
    voting:
      "Early voting began Sept. 24 and expanded to more sites Oct. 19 through Nov. 2; same-day registration is available. Mail ballots postmarked by Nov. 3 count if received by Nov. 17.",
  },
  IN: {
    news: [
      "U.S. House races use the 2021 district map; after Trump-endorsed challengers ousted several GOP state senators who blocked redistricting, Senate leader Rodric Bray said July 31 he would step down, and Senate Republicans endorsed Sen. Chris Garten to succeed him.",
      "The Indiana Supreme Court on Sept. 10 declared Sen. Spencer Deery, a redistricting opponent, the three-vote winner of his GOP primary over a Trump-endorsed challenger.",
    ],
    races: {},
    voting:
      "Early in-person voting runs Oct. 6 to noon Nov. 2, including Saturdays Oct. 24 and 31. Voting by mail requires a qualifying excuse, such as being 65 or older, and mail ballots must arrive by 6 p.m. Nov. 3.",
  },
  KS: {
    news: [
      "On Aug. 4, voters rejected, 61% to 39%, a constitutional amendment to have state Supreme Court justices directly elected.",
      "In the Aug. 4 primaries, Masterson won a seven-way GOP race and Holscher defeated Kelly-backed Sen. Ethan Corson.",
    ],
    races: {
      governor:
        "Open seat; Gov. Laura Kelly is term-limited. Republican Ty Masterson, the Trump-endorsed state Senate president, has emphasized limiting local property-tax increases and literacy reforms, and opposes Medicaid expansion. Democratic state Sen. Cindy Holscher has emphasized fully funding special education, expanding Medicaid and pausing new data centers.",
      senate:
        "Republican Sen. Roger Marshall seeks a second term, emphasizing support for Trump's tariffs and border policies and linking his opponent to Senate Democratic leader Chuck Schumer. Democrat Adam Hamilton, founding pastor of Leawood's Church of the Resurrection, calls himself an independent-minded Democrat, has pledged not to back Schumer as leader and has emphasized ending the Iran war. They debated twice.",
    },
    voting:
      "Advance voting began Oct. 14 and must be offered in every county by Oct. 27. Under an Oct. 2 appeals court ruling, mail ballots postmarked by Nov. 3 count if received by Nov. 6.",
  },
  KY: {
    news: [
      "Amendment 1 would bar governors from granting pardons or commutations from 60 days before a gubernatorial election until the new term begins.",
      "Gov. Andy Beshear signed an Aug. 6 executive order aimed at keeping data centers from shifting power costs to other customers, and later urged repealing their state tax breaks.",
    ],
    races: {
      senate:
        "Open seat; Sen. Mitch McConnell is retiring. Republican U.S. Rep. Andy Barr, endorsed by Trump, has emphasized lowering costs through limited government, expanded energy production including coal, and support for agriculture. Democrat Charles Booker, a former Louisville state representative, has emphasized fighting poverty, health care as a human right, union jobs and lower grocery and gas prices. They debated once, on Oct. 19.",
    },
    voting:
      "No-excuse early in-person voting is limited to Oct. 29–31; excused in-person absentee voting ran Oct. 21–23 and 26–28. Mail voting requires an excuse, and mail ballots must arrive by 6 p.m. local time Nov. 3.",
  },
  LA: {
    news: [
      "In August, Gulf States Newsroom reported that Letlow bought Meta stock after signing a 2024 nondisclosure agreement about Meta's Richland Parish data center and disclosed the trades late; her spokesperson said she was unaware of them.",
      "U.S. House races appear on the Nov. 3 ballot as open primaries, with any runoffs Dec. 12, under a new map that eliminated one of two majority-Black districts.",
    ],
    races: {
      senate:
        "The seat is open after Republican Sen. Bill Cassidy lost the May GOP primary; Republican U.S. Rep. Julia Letlow and Democrat Jamie Davis, a Tensas Parish farmer and former police juror, won their parties' June 27 runoffs. Letlow, endorsed by President Trump, has emphasized her appropriations record, flood insurance costs, workforce training and parental rights in education. Davis has emphasized electricity, insurance and health care costs and banning nondisclosure agreements for public officials.",
    },
    voting:
      "Early voting ran Oct. 20-27. Mail absentee voting requires a qualifying reason, such as being 65 or older; requests are due Oct. 30, and ballots must reach parish registrars by 4:30 p.m. Nov. 2.",
  },
  MA: {
    news: [
      "The ballot carries a record nine statewide questions, including measures on retail marijuana sales, a 2024 gun law, all-party primaries, Election Day registration and applying public records law to the Legislature.",
    ],
    races: {
      governor:
        "Democratic Gov. Maura Healey seeks a second term against Republican Mike Minogue, a former medical-device CEO who won the Sept. 1 primary. Healey has emphasized housing construction, health care costs, abortion protections and resisting Trump immigration enforcement; Minogue has emphasized cutting energy costs with gas pipelines, easing building rules and cooperating with federal immigration authorities.",
      senate:
        "Democratic Sen. Ed Markey, who defeated U.S. Rep. Seth Moulton in the Sept. 1 primary, seeks a third full term against Republican John Deaton, an attorney and the 2024 nominee. Markey says he would check President Trump and backs Medicare for All; Deaton calls himself an independent-minded Republican and backs nuclear power and natural gas.",
    },
    voting:
      "Early voting runs Oct. 17-30. Mailed ballots postmarked by Nov. 3 count if received by Nov. 6; drop-box and hand-delivered ballots are due by 8 p.m. Nov. 3.",
  },
  MD: {
    news: [
      "Voters will decide Question 3, a constitutional amendment passed in an August special session that would exempt congressional maps from state constitutional districting rules; Moore supports it, and Republican leaders oppose it.",
      "Moore and Cox met Oct. 6 in their only debate, clashing over affordability, data centers, Cox's ties to Trump and Moore's commitment to serving a full term.",
    ],
    races: {
      governor:
        "Democratic Gov. Wes Moore seeks a second term in a rematch with Republican Dan Cox, a former state delegate who won the June 23 GOP primary over Ed Hale. Moore has emphasized middle-class tax cuts and falling violent crime and ties Cox to President Trump; Cox has pledged not to raise taxes or fees and blames Moore for a projected state budget deficit.",
    },
    voting:
      "Early voting runs Oct. 22-29, and eligible residents can register and vote the same day during early voting or on Election Day. Any voter may vote by mail; ballots must be postmarked by Nov. 3 or placed in a drop box by 8 p.m. that day.",
  },
  ME: {
    news: [
      "Democratic primary winner Graham Platner withdrew July 10 after a sexual assault allegation; Democrats nominated Jackson July 25.",
      "ProPublica reported in September that a former defense-contractor CEO told the FBI he traded donations for Collins' help on contracts; Collins denies wrongdoing.",
    ],
    races: {
      governor:
        "Open seat. Democrat Hannah Pingree, a former House speaker, emphasizes housing and energy efficiency; Republican Bobby Charles, a former U.S. assistant secretary of state, emphasizes cutting energy costs and regulations; independent state Sen. Rick Bennett, a former state GOP chair, presents himself as an alternative to both parties.",
      senate:
        "Republican Sen. Susan Collins, in the Senate since 1997, seeks a sixth term against Democrat Troy Jackson, a logger and former state Senate president who became the nominee after primary winner Graham Platner withdrew in July. Collins won her fifth term in 2020 by 8.6 points while Joe Biden carried Maine by 9. Collins emphasizes her Appropriations chairmanship and a rural health fund she authored; Jackson backs Medicare for All and faults her vote advancing the 2025 GOP law that cut Medicaid, which she opposed on final passage.",
    },
    voting:
      "No-excuse absentee requests and in-person absentee voting end Oct. 29; ballots are due 8 p.m. Nov. 3; same-day registration is available. Ranked-choice voting applies to the Senate race, not governor.",
  },
  MI: {
    news: [
      "Voters will decide Proposal 1, on calling a state constitutional convention, and Proposal 2, which would bar campaign contributions from regulated utilities and large government contractors.",
    ],
    races: {
      governor:
        "Open seat. Democrat Jocelyn Benson, the secretary of state, has emphasized public education and health and child care costs and ties James to Trump's policies; Republican U.S. Rep. John James, who defeated Perry Johnson in the primary, has emphasized declining test scores, energy costs and local control over data centers and blames Democrats' record.",
      senate:
        "Open seat. Democrat Abdul El-Sayed, a physician and former public health official, narrowly defeated U.S. Rep. Haley Stevens in the Aug. 4 primary; Republican Mike Rogers, a former congressman, was unopposed. El-Sayed has emphasized health care and living costs and ties Rogers to Trump; Rogers has emphasized border security and says El-Sayed's Medicare for All plan would raise taxes.",
    },
    voting:
      "Early in-person voting runs Oct. 24-Nov. 1. Any voter may vote absentee, with ballots due to clerks by 8 p.m. Nov. 3, and residents can register and vote at clerks' offices through Election Day.",
  },
  MN: {
    news: [
      "Flanagan and Tafoya held their only scheduled debate Oct. 16, and Klobuchar and Demuth debated Oct. 21 in Rochester.",
    ],
    races: {
      governor:
        "Open seat. DFL U.S. Sen. Amy Klobuchar has emphasized lowering health care costs and building housing and ties Demuth to Trump; Republican House Speaker Lisa Demuth, who defeated Trump-endorsed Mike Lindell in the primary, has emphasized cracking down on fraud and ties Klobuchar to Gov. Tim Walz. Both have pledged not to raise taxes.",
      senate:
        "Open seat. DFL Lt. Gov. Peggy Flanagan defeated U.S. Rep. Angie Craig in the Aug. 11 primary; Republican Michele Tafoya, a former sportscaster and commentator, won her primary and was endorsed by President Trump in October. Flanagan has emphasized fighting corporate influence and opposing tariffs and calls Tafoya a rubber stamp for Trump; Tafoya calls herself an independent thinker and faults Flanagan over fraud in state programs.",
    },
    voting:
      "Minnesotans can vote early in person through Nov. 2. Mailed absentee ballots must arrive by 8 p.m. Nov. 3 (hand-delivered ones by 5 p.m.), and Election Day registration is available at polling places.",
  },
  MO: {
    news: [
      "U.S. House races use the 2022 congressional map, which keeps Kansas City in one district, rather than the 2025 map used in the August primary, after a Sept. 3 Missouri Supreme Court ruling and later U.S. Supreme Court orders; Proposition A asks voters whether to approve the 2025 map.",
      "Amendment 3 would repeal the 2024 abortion-rights amendment, allow abortion only for emergencies, fetal anomalies, or rape or incest before 12 weeks, and ban gender transition procedures for minors.",
    ],
    races: {},
    voting:
      "No-excuse in-person absentee voting runs Oct. 20-Nov. 2 with photo ID; mail absentee voting requires an excuse, and mailed ballots must arrive by 7 p.m. Nov. 3.",
  },
  MS: {
    news: [
      "On June 29, the U.S. Supreme Court ruled 5-4 in a Mississippi case that the state may count mail ballots postmarked by Election Day and received up to five business days later.",
      "President Trump rallied for Hyde-Smith in Corinth on Oct. 26; former Vice President Kamala Harris campaigned with Colom on Oct. 22, and former President Bill Clinton is scheduled to join him in Tupelo on Oct. 30.",
    ],
    races: {
      senate:
        "Republican Sen. Cindy Hyde-Smith, seeking a second full term, faces Democrat Scott Colom, a district attorney, and independent Ty Pinkins, the Democrats' 2024 Senate nominee. Hyde-Smith has highlighted her record on rural health care, national security and law enforcement; Colom has emphasized raising wages, lowering costs and protecting rural hospitals; Pinkins presents himself as an alternative to both parties.",
    },
    voting:
      "Mississippi has no no-excuse early voting: absentee voting requires an excuse such as being 65 or older, in-person absentee voting at circuit clerks' offices ends Oct. 31, and mailed ballots must be postmarked by Nov. 3 and arrive within five business days.",
  },
  MT: {
    news: [
      "Alme and Bankhead won their parties' June 2 primaries, Bankhead in a five-way race.",
      "Voters will decide CI-132 (nonpartisan judicial elections), CI-133 (protecting initiative and referendum powers) and I-194 (barring political spending by corporations and other entities).",
    ],
    races: {
      senate:
        "Open seat after Republican Sen. Steve Daines withdrew. Republican Kurt Alme, a former U.S. attorney endorsed by Daines and President Trump, emphasizes reining in federal spending and border security; independent Seth Bodnar, an Army veteran and former University of Montana president backed by former Democratic Sen. Jon Tester, emphasizes housing costs and says only an independent can fix a broken system; Democrat Alani Bankhead, an Air Force veteran, backs universal health care, taxing the wealthy and a moratorium on AI data centers. Libertarian Kyle Austin is also on the ballot.",
    },
    voting:
      "Any voter may vote absentee, with ballots due by 8 p.m. Nov. 3, and residents can register and vote in person at county election offices through Election Day.",
  },
  NC: {
    news: [
      "Voters will decide constitutional amendments to require photo ID for all voters, including by mail, cap the state income tax rate at 3.5% and require limits on local property tax increases.",
      "Remnants of Hurricane Isaias brought flooding and landslides to western North Carolina in mid-October, and two nor'easters damaged N.C. 12 on the Outer Banks, prompting an Oct. 22 state of emergency.",
    ],
    races: {
      senate:
        'Democrat Roy Cooper, a former two-term governor, and Republican Michael Whatley, a former Republican National Committee chairman, seek the open seat of retiring Republican Sen. Thom Tillis. Cooper has centered his campaign on affordability under the slogan "Make Stuff Cost Less," including opposing tariffs and restoring health insurance subsidies; Whatley has emphasized crime, including limits on pretrial release, and more recently lower taxes and energy production under "Make More Keep More." The two have not debated.',
    },
    voting:
      "Early voting, with same-day registration, runs Oct. 15 through 3 p.m. Oct. 31; absentee ballots, which any voter could request through Oct. 20, must be received by 7:30 p.m. Nov. 3. Photo ID is required.",
  },
  ND: {
    news: [
      "Voters will decide Measure 1, requiring 60% approval for future constitutional amendments, and Measure 2, a citizen initiative requiring free school breakfast and lunch funded by the state.",
    ],
    races: {},
    voting:
      "North Dakota has no voter registration, but voters must show ID. Absentee ballots, available without an excuse, must reach the county auditor by the end of voting on Nov. 3; early in-person voting is offered only in seven counties, including Burleigh, Cass, Grand Forks and Ward.",
  },
  NE: {
    news: [
      "Statewide measures would legalize online sports betting, constitutionally require sex-separate school sports and allow legislators three consecutive terms.",
      "An outside group's ad on Ricketts's 2022 vote as governor to pardon a man convicted of sexually assaulting his then-wife, which Ricketts defended, and leaked Osborn remarks about a hypothetical rural Trump supporter, for which he apologized, drew wide attention.",
    ],
    races: {
      governor:
        "Republican Gov. Jim Pillen seeks a second term against Democrat Lynne Walz and America First nominee Brett Lindstrom, both former state senators. Pillen has emphasized property tax relief, agriculture and spending cuts; Walz, affordability, the budget shortfall and trust in government; Lindstrom, property tax changes.",
      senate:
        "Republican Sen. Pete Ricketts faces independent Dan Osborn, a former union local president backed by Nebraska Democrats, whose nominee withdrew. Ricketts has emphasized the 2025 federal tax cuts, border security and law enforcement; Osborn, living costs, tariffs, a congressional stock-trading ban and independence from both parties.",
    },
    voting:
      "Early in-person voting at county election offices runs Oct. 5–Nov. 2; mail ballots must arrive by 8 p.m. CT (7 p.m. MT) on Nov. 3.",
  },
  NH: {
    news: [
      "Every town's ballot asks whether to cap local school property tax growth for two years, which requires 60% approval.",
      "Pappas and Sununu debated Oct. 28; Ayotte and Warmington debated Oct. 20, with a final WMUR debate set for Oct. 29.",
    ],
    races: {
      governor:
        "Republican Gov. Kelly Ayotte seeks a second two-year term against Democrat Cinde Warmington, a former executive councilor. Ayotte has emphasized housing supply, a local property tax cap and opposing income and sales taxes; Warmington has emphasized housing costs she ties to private equity, health care and higher education funding.",
      senate:
        "Democratic Rep. Chris Pappas and Republican former Sen. John E. Sununu seek retiring Democratic Sen. Jeanne Shaheen's open seat. Pappas has emphasized costs he blames on tariffs and the Iran war; Sununu has emphasized his experience, energy infrastructure and inflation he blames on Biden-era policies; they have clashed over Social Security.",
    },
    voting:
      "New Hampshire has no early voting; absentee voting requires a reason such as absence or disability, and mailed absentee ballots must arrive by 5 p.m. Nov. 3. Election Day registration is available.",
  },
  NJ: {
    news: [
      "Murphy won a four-way Republican primary on June 2; Booker was unopposed.",
      "In their only debate, on Oct. 8 at Rider University, the two split over the Iran war, immigration enforcement and Trump.",
    ],
    races: {
      senate:
        "Democratic Sen. Cory Booker, seeking a third full term, faces Republican Justin Murphy, an attorney, Navy veteran and former Tabernacle deputy mayor. Booker has emphasized affordability, a plan to make the first $75,000 of household income tax-free, opposition to Trump's tariffs and ending the Iran war; Murphy has emphasized tax cuts including a flat tax, domestic energy production, school choice and support for the Iran war.",
    },
    voting:
      "Early in-person voting runs Oct. 24–Nov. 1; mail ballots must be postmarked by Nov. 3 and received by Nov. 9, or placed in a drop box by 8 p.m. Election Day.",
  },
  NM: {
    news: [
      "The governor candidates did not debate after a dispute over dates; Haaland cited a prior commitment when Hull appeared alone on KOB-TV Oct. 8.",
      "Voters will decide four constitutional amendments, including paying legislators and ending the governor's pocket veto, and three bond questions.",
    ],
    races: {
      governor:
        "Democrat Deb Haaland, a former U.S. interior secretary, and Republican Gregg Hull, a former Rio Rancho mayor, seek to succeed term-limited Democratic Gov. Michelle Lujan Grisham. Haaland has emphasized a higher minimum wage, expanded tax credits, early literacy and faster housing permits; Hull has emphasized phasing out the personal income tax, overhauling the child welfare agency and adding police.",
      senate:
        "Democratic Sen. Ben Ray Luján faces Republican Larry Marker, a Roswell oil and gas businessman nominated as a write-in. Luján has emphasized living costs, water, opposition to Medicaid cuts and tariffs; Marker has emphasized federal debt, stabilizing Social Security, oil and gas production and border security.",
    },
    voting:
      "Early voting runs Oct. 6–31, with same-day registration then and on Election Day; mail ballots must be received by 7 p.m. Nov. 3.",
  },
  NV: {
    news: [
      "Voters will decide for a second time Question 6, putting abortion rights in the state constitution, and Question 7, requiring voter ID; both passed in 2024.",
      "There was no governor debate: Aaron Ford accepted two news-outlet debates that Lombardo declined, and Lombardo accepted a radio-show debate that Ford declined.",
    ],
    races: {
      governor:
        "Republican Gov. Joe Lombardo, a former Clark County sheriff, faces Democratic Attorney General Aaron Ford and nonpartisan Danielle Ford, a State Board of Education member. Lombardo has emphasized his no-new-taxes pledge, down-payment aid for homebuyers, school funding and stricter drug-trafficking penalties; Aaron Ford has emphasized affordability, limits on corporate home buying, raising school funding without new taxes and criticism of data center tax incentives; Danielle Ford has emphasized education and ending two-party dominance.",
    },
    voting:
      "Every active registered voter is mailed a ballot, which must be postmarked by Nov. 3 and received by 5 p.m. Nov. 7, or dropped off by 7 p.m. Election Day; early in-person voting runs Oct. 17–30, and same-day registration is available in person.",
  },
  NY: {
    news: [
      "Hochul and Blakeman held their only debate Oct. 13 on Spectrum News, clashing over immigration enforcement, energy costs, crime and Trump.",
      "All three House candidates backed by Mamdani won June 23 Democratic primaries, including Brad Lander, who defeated Rep. Dan Goldman.",
    ],
    races: {
      governor:
        "Democratic Gov. Kathy Hochul, also on the Working Families line, faces Republican Bruce Blakeman, the Nassau County executive, also on the Conservative line. Hochul has emphasized middle-class tax cuts, energy rebate checks, free school meals and falling crime, and links Blakeman to Trump; Blakeman has emphasized exempting the first $50,000 of income ($100,000 for couples) from state income tax, cutting utility bills partly by lifting the fracking ban, and ending sanctuary policies, and links Hochul to New York City Mayor Zohran Mamdani.",
    },
    voting:
      "Early voting runs Oct. 24–Nov. 1; voters may still request a mail ballot in person through Nov. 2, and mailed ballots must be postmarked by Nov. 3 and received by Nov. 10.",
  },
  OH: {
    news: [
      "A 90-day state gas-tax suspension took effect Oct. 4 after both governor candidates sought it.",
      "Issue 3, the only statewide measure, would add Ohio's existing voter photo ID requirement to the constitution.",
    ],
    races: {
      governor:
        'Open seat; Gov. Mike DeWine is term-limited. Democrat Amy Acton, a physician and former state health director, has emphasized a "working families tax cut," electric bills and senior property-tax relief; Republican Vivek Ramaswamy, an entrepreneur and 2024 presidential candidate, has emphasized rolling back property taxes and phasing out the income tax.',
      senate:
        "Special election for the rest of JD Vance's term. Republican Sen. Jon Husted, the former lieutenant governor appointed in 2025, faces Democrat Sherrod Brown, a former three-term senator who lost in 2024. Husted has emphasized immigration, inflation and Brown's decades in Washington; Brown has emphasized grocery, gas and health costs and what he calls a rigged system.",
    },
    voting:
      "Ohio has no-excuse absentee voting; early in-person voting runs Oct. 6-Nov. 1. Mail-ballot requests were due Oct. 27; absentee ballots must reach county boards of elections by 7:30 p.m. Nov. 3.",
  },
  OK: {
    news: [
      "Mazzei, endorsed by President Trump, defeated Attorney General Gentner Drummond 50.3% to 49.7% in the Aug. 25 GOP runoff.",
      "State Question 845 would change the Judicial Nominating Commission; State Question 847 would lower caps on annual property-value increases.",
    ],
    races: {
      governor:
        "Open seat; Gov. Kevin Stitt is term-limited. Republican Mike Mazzei, a Tulsa financial planner and former state senator who chaired the Senate Finance Committee and was Stitt's budget secretary, won the nomination with President Trump's endorsement and has emphasized eliminating the income tax and property taxes on seniors' and veterans' homes; Democrat Cyndi Munson, the state House minority leader, has emphasized public school funding, teacher pay and ending the private-school tax credit.",
      senate:
        "Open seat after Markwayne Mullin resigned in March to become Homeland Security secretary. Republican Rep. Kevin Hern has emphasized extending tax cuts, border security, school choice and a balanced-budget amendment; Democrat N'Kiyla Jasmine Thomas, a nurse in her first race, has emphasized health care access, the nursing shortage, reproductive rights and tribal sovereignty.",
    },
    voting:
      "Early in-person voting runs Oct. 28-31; absentee voting requires no excuse. Requests were due Oct. 19, and mailed ballots must be received by 7 p.m. Nov. 3.",
  },
  OR: {
    news: [
      "Wildfires burned a record 2.1 million-plus acres this season, destroying 56 homes and killing two fire workers.",
      "No statewide measures are on the ballot; Kotek and Drazan held three debates, the last Oct. 13.",
    ],
    races: {
      governor:
        "Democratic Gov. Tina Kotek faces Republican state Sen. Christine Drazan in a rematch of 2022, when Kotek won 47.0% to 43.6% and independent Betsy Johnson took 8.6%. Kotek has emphasized opposing Trump administration policies and protecting the Oregon Health Plan, abortion access and vote by mail; Drazan has emphasized Kotek's record on housing and homelessness, opposing new taxes and loosening building and land-use rules.",
      senate:
        "Democratic Sen. Jeff Merkley, first elected in 2008, faces Republican state Sen. David Brock Smith. Merkley has emphasized opposing what he calls the Trump administration's erosion of the Constitution and the costs of housing, health care and education; Brock Smith has emphasized affordability, no new taxes, public safety and wildfire prevention.",
    },
    voting:
      "Oregon votes by mail; ballots went out Oct. 14 and must reach a drop box or county office by 8 p.m. Nov. 3, or be postmarked by Nov. 3 and arrive by Nov. 10.",
  },
  PA: {
    news: [
      'After the state Supreme Court ruled in June that "skill games" are illegal slot machines, lawmakers did not legalize them by an Oct. 13 deadline, and the machines became subject to seizure.',
      "A measles outbreak that began in late April has reached nearly 1,400 confirmed cases in 44 counties, with five deaths, according to the state Health Department.",
    ],
    races: {
      governor:
        "Democratic Gov. Josh Shapiro, elected in 2022 by 15 points (56.5% to 41.7%), faces Republican Stacy Garrity, a retired Army Reserve colonel in her second term as state treasurer after winning statewide in 2020 and 2024. Shapiro is endorsed by the state AFL-CIO, building-trades unions, Fraternal Order of Police lodges, the state troopers' association and firefighters; Garrity by President Trump, the state Republican Party and anti-abortion groups. Shapiro has emphasized increased school funding, data-center rules requiring local approval, a $15 minimum wage and opposition to President Trump; Garrity has emphasized a data-center pause, ending property taxes for seniors, veterans and first responders, school choice and government transparency. They debated once, on Oct. 7.",
    },
    voting:
      "Pennsylvania has no traditional early voting, but any voter could request a no-excuse mail ballot, including in person at county election offices, through Oct. 27. Mail ballots must be received by 8 p.m. Nov. 3; postmarks do not count.",
  },
  RI: {
    news: [
      "McKee's June 12 budget phases in a higher tax on income over $1 million.",
      "Voters will decide five bond questions totaling $600 million for higher education, housing, economic development, culture and environmental projects.",
    ],
    races: {
      governor:
        "Democrat Helena Foulkes, a former CVS executive, defeated Gov. Dan McKee 62.5% to 37.5% in the Sept. 9 primary and faces Republican Aaron Guckian, a former dental association director, and independent Ken Block, a software firm founder. Foulkes has emphasized building 20,000 middle-class homes; Guckian, lowering taxes and fees; Block, cutting government waste.",
      senate:
        "Democratic Sen. Jack Reed, in office since 1997, faces Republican Raymond McKay, a former Warwick city administrator. Reed has emphasized ending what he calls an unauthorized Iran war, opposing tariffs, and housing and health costs; McKay has emphasized nuclear and fossil-fuel energy, balancing the budget to protect Social Security and Medicare, and border security.",
    },
    voting:
      "Early in-person voting runs Oct. 14-Nov. 2; mail-ballot applications were due Oct. 13, and ballots must reach the Board of Elections or a drop box by 8 p.m. Nov. 3.",
  },
  SC: {
    news: [
      "Graham won the GOP Senate nomination in an Aug. 25 runoff over Rep. Ralph Norman.",
      "Wilson won the June 23 GOP runoff for governor over Lt. Gov. Pamela Evette.",
    ],
    races: {
      governor:
        "Open seat; Gov. Henry McMaster is term-limited. Republican Attorney General Alan Wilson has emphasized phasing out the income tax, capping spending growth and auditing the transportation department; Democratic state Rep. Jermaine Johnson has emphasized repealing this year's income tax law, which he says raised taxes on low earners, and a data-center moratorium.",
      senate:
        "Republican Sen. Darline Graham, the state's former commissioner for the blind, was appointed after her brother, Lindsey Graham, died July 11. She faces Democrat Annie Andrews, a pediatrician. Graham has emphasized the 2025 GOP tax law, AI competition with China and opposing abortion; Andrews has emphasized opposing the Iran war, a public insurance option and protecting Social Security.",
    },
    voting:
      "Early in-person voting runs Oct. 19-31 (Monday-Saturday); mail voting requires a reason, such as being 65 or older. Applications were due Oct. 23; ballots must arrive by 7 p.m. Nov. 3.",
  },
  SD: {
    news: [
      "Rhoden beat businessman Toby Doeden 71% to 29% in the July 28 GOP runoff, the state's first for governor.",
      "Four amendments are on the ballot, including conditioning Medicaid expansion on 90% federal funding and requiring 60% approval for future amendments.",
    ],
    races: {
      governor:
        "Republican Gov. Larry Rhoden, who succeeded Kristi Noem in 2025, faces Democrat Dan Ahlers, a former legislator. Rhoden has emphasized this year's property tax cuts and a data-center law; Ahlers has emphasized more state school funding to ease property taxes, free school lunches and a child care tax credit.",
      senate:
        "Republican Sen. Mike Rounds, a former governor seeking a third term, faces independent Brian Bengs, a retired Air Force officer and the 2022 Democratic nominee, after Democrat Julian Beaudion withdrew. Rounds has emphasized AI competition with China and requiring data centers to cover their power costs; Bengs has emphasized campaign finance reform, a Medicare buy-in and pausing data-center construction.",
    },
    voting:
      "No-excuse absentee voting, in person or by mail, runs Sept. 18-Nov. 2; mailed ballots must arrive by 7 p.m. local time Nov. 3.",
  },
  TN: {
    news: [
      "Federal judges declined in July to block, and in October dismissed a challenge to, the new congressional map splitting Memphis among three districts.",
      "Three amendments would expand when bail can be denied, bar a state property tax and expand crime victims' rights.",
    ],
    races: {
      governor:
        "Open seat; Gov. Bill Lee is term-limited. Republican Sen. Marsha Blackburn has emphasized immigration enforcement, transportation, rural health care and expanding private-school vouchers; Democrat Jerri Green, a Memphis City Council member and interim county public defender, has emphasized ending the grocery tax, Medicaid expansion and a data-center moratorium. Independent manufacturer David Hatley is also running.",
      senate:
        "Republican Sen. Bill Hagerty faces Democrat Marquita Bradshaw, a Memphis environmental activist he defeated in 2020. Hagerty has emphasized low taxes, spending cuts, energy production and President Trump's immigration policies; Bradshaw has emphasized affordable housing and health care, rural hospitals and public schools.",
    },
    voting:
      "Early voting runs Oct. 14-29; mail voting requires an excuse, such as being 60 or older. Requests were due Oct. 24; mailed ballots must arrive by poll closing Nov. 3.",
  },
  TX: {
    news: [
      'Paxton and Abbott did not debate their opponents; Talarico held a solo CNN town hall and Hinojosa a solo "debate" event.',
      "In August, federal officials paused border-barrier work in Big Bend National Park after public opposition; both Senate nominees backed the pause.",
    ],
    races: {
      governor:
        "Republican Gov. Greg Abbott seeks a fourth term against Democratic state Rep. Gina Hinojosa. Abbott has emphasized property tax cuts and border security; Hinojosa has emphasized public school funding, ending the state's school voucher program and $1,500 payments to families.",
      senate:
        "Open seat: Republican Attorney General Ken Paxton, who defeated Sen. John Cornyn in the May runoff, faces Democratic state Rep. James Talarico. Talarico has campaigned on affordability and political reform and accused Paxton of corruption; Paxton has touted his record suing tech and drug companies, proposed tax deductions and cast Talarico as a liberal.",
    },
    voting:
      "Early in-person voting runs Oct. 19–30. Mail voting is limited to eligible voters, such as those 65 or older or disabled; mail ballots must arrive by Nov. 3 (Nov. 4 if postmarked by Election Day).",
  },
  UT: {
    news: [
      "Under a court-ordered congressional map used for the first time this year, former Rep. Ben McAdams won the June 23 Democratic primary in the redrawn, Salt Lake County-based 1st District.",
      "Voters will decide Amendment A, which would end a requirement to print proposed constitutional amendments in newspapers for two months, and Amendment B, which would require 60% approval for citizen initiatives that raise taxes.",
    ],
    races: {},
    voting:
      "Ballots were mailed to all active registered voters starting Oct. 13 and must reach county clerks or a drop box by 8 p.m. Nov. 3; postmarks do not count. In-person early voting began Oct. 20, with dates varying by county, and voters can register and vote provisionally in person through Election Day.",
  },
  VA: {
    news: [
      "Mizusawa won the Aug. 4 Republican primary with 51% of the vote over two rivals; Warner was unopposed.",
      "Voters will decide constitutional amendments establishing a right to make decisions about pregnancy, birth control and abortion (with third-trimester limits allowed), repealing the same-sex marriage ban, and automatically restoring voting rights to people with felony convictions upon release.",
    ],
    races: {
      senate:
        "Democratic Sen. Mark Warner, a former governor seeking a fourth term he says would be his last, faces Republican Bert Mizusawa, a retired Army major general. Warner has emphasized universal health coverage, standards for AI and data centers, and ending the Iran war and tariffs to lower prices; Mizusawa has emphasized his national security experience and ties to President Trump, domestic energy production, deficit reduction and a gradual return to a gold standard.",
    },
    voting:
      "Early in-person voting runs Sept. 18–Oct. 31. Any voter could request a mail ballot through Oct. 23; mailed ballots must be postmarked by Nov. 3 and received by noon Nov. 6. Same-day registration, with a provisional ballot, is available.",
  },
  VT: {
    news: [
      "Janoo defeated Aly Richards by about 2,000 votes in the Aug. 11 Democratic primary; Scott and Janoo held their first televised debate on Vermont Public on Oct. 21.",
      "Voters will decide two constitutional amendments: Proposal 3 would establish a right to collectively bargain, and Proposal 4 would bar unequal treatment based on race, sex, religion, sexual orientation, gender identity and other traits.",
    ],
    races: {
      governor:
        "Republican Gov. Phil Scott seeks a sixth two-year term against Amanda Janoo, an economist and first-time candidate running as the Democratic/Progressive nominee. Scott has emphasized opposing new taxes, education reform including school consolidation, and more housing; Janoo has proposed free universal primary care funded by a surcharge on incomes above $250,000 and higher taxes on second homes and short-term rentals.",
    },
    voting:
      "Ballots were mailed to all active registered voters in early October and must reach town clerks by 7 p.m. Nov. 3; officials advise using drop boxes or hand delivery rather than mail. Voters can also vote early at clerk offices or in person Nov. 3, with same-day registration available.",
  },
  WA: {
    news: [
      "Voters will decide three statewide initiatives: IP26-645 would repeal the 9.9% tax on individual income over $1 million and bar income taxes; IL26-001 would restore the 2024 parents' rights law as originally enacted; and IL26-638 would bar students defined as biologically male from girls' school sports.",
      "Three wildfires on the outskirts of Spokane in early August forced tens of thousands of residents to evacuate and destroyed hundreds of homes.",
    ],
    races: {},
    voting:
      "Ballots were mailed to all registered voters by Oct. 16 and must be placed in an official drop box by 8 p.m. Nov. 3 or mailed with a postmark no later than Election Day. Voters can register in person at county voting centers through 8 p.m. on Election Day.",
  },
  WI: {
    news: [
      "Crowley reentered the Democratic race with Evers' endorsement in July after Lt. Gov. Sara Rodriguez withdrew, then defeated state Rep. Francesca Hong in the Aug. 11 primary by about 3,700 votes.",
      "The nominees held their only debate Oct. 21 in Milwaukee, clashing over taxes, data centers, school vouchers, immigration enforcement and abortion.",
    ],
    races: {
      governor:
        "Open seat: Democratic Gov. Tony Evers is not seeking a third term. Democrat David Crowley, the Milwaukee County executive, faces Republican U.S. Rep. Tom Tiffany of the 7th District. Crowley has emphasized affordability, more public school funding without expanding vouchers, expanding BadgerCare and local control over data centers; Tiffany has proposed returning the projected state surplus through income and property tax cuts, ending state taxes on tips and overtime, and no subsidies for data centers.",
    },
    voting:
      "In-person absentee voting began Oct. 20 and may run through Nov. 1, with hours set by municipal clerks; mailed absentee ballots must be received by 8 p.m. Nov. 3. Voters can register in person on Election Day with proof of residence.",
  },
  WV: {
    news: [
      "Voters will decide Amendment 1, which would state in the West Virginia Constitution that only U.S. citizens may vote; state law already requires citizenship to register.",
      "Repeated summer storms caused flooding in June, July and August that killed at least three people, and Gov. Patrick Morrisey declared a statewide state of emergency on Aug. 18.",
    ],
    races: {
      senate:
        "Republican Sen. Shelley Moore Capito, chair of the Environment and Public Works Committee, is seeking a third term against Democrat Rachel Fetty Anderson, a Morgantown attorney and former city council member. Capito has emphasized her committee posts, tax relief in the 2025 federal tax-and-spending law and energy permitting reform; Fetty Anderson has said Capito's votes favor donors and corporate interests, criticized health coverage losses under that law and tied fuel prices to the Iran war and tariffs.",
    },
    voting:
      "Early in-person voting runs Oct. 21–31. Mail voting requires an eligible reason such as illness, disability or travel, with applications due Oct. 28; mailed ballots must be postmarked by Nov. 3 and received by Nov. 9.",
  },
  WY: {
    news: [
      "Barlow won the Aug. 18 GOP primary over Trump-endorsed Superintendent Megan Degenfelder and state party-endorsed Brent Bien.",
      "Voters will decide Proposition 1, a citizen initiative exempting half the assessed value of qualifying primary residences from property taxes.",
    ],
    races: {
      governor:
        "Open seat (Gov. Mark Gordon is term-limited): Republican state Sen. Eric Barlow, a rancher and former House speaker, faces Democrat Kenneth Casner, a past candidate for governor and Senate. Barlow has emphasized energy, rural health care and recent property tax cuts; Casner has focused on working-class residents and refusing campaign donations.",
      senate:
        "Open seat (Sen. Cynthia Lummis is retiring): Republican U.S. Rep. Harriet Hageman faces Democrat James Byrd, a former state legislator. Hageman has emphasized regulatory reform, legacy energy industries, cutting federal spending and state authority; Byrd has emphasized keeping federal lands public, protecting water rights and rural hospitals, and making data centers pay for grid upgrades.",
    },
    voting:
      "No-excuse absentee voting, by mail or at county clerk offices, runs Oct. 6–Nov. 2; ballots must arrive by 7 p.m. Nov. 3. Election Day registration is available.",
  },
};

/** Each state's Senate and governor races, per feed (none where neither is on the ballot). */
export const STATE_FEEDS: Partial<
  Record<string, Record<NewsFeed, FeedItem[]>>
> = {
  AK: {
    left: [
      {
        headline:
          "'Corruption' and 'desperation': Peltola slams Trump DOJ lawsuit over Alaska Senate race",
        outlet: "MS NOW",
        summary:
          "Peltola called Trump's extraordinary lawsuit to block certification if Sullivan narrowly loses 'a very desperate attempt to cling to power'; GOP Sen. Lisa Murkowski also criticized the DOJ.",
      },
      {
        headline:
          "Check Out Alaska Ballot And See Why Sullivan Controversy Is A Nonissue",
        outlet: "HuffPost",
        summary:
          "The ballot clearly labels the senator as the Republican incumbent, undercutting Trump's lawsuit; HuffPost notes spoiler recruiting is generally legal and Peltola calls the Times report a 'distraction.'",
      },
      {
        headline:
          "Murkowski and Murkowski endorse Alaska's Democratic governor candidate",
        outlet: "Alaska Beacon",
        summary:
          "Republican Sen. Lisa Murkowski and her father, ex-Gov. Frank Murkowski, endorsed Democrat Jonathan Kreiss-Tomkins, with Frank joining former Govs. Tony Knowles and Bill Walker in backing him.",
      },
    ],
    right: [
      {
        headline:
          "Mary Peltola 'definitely aware' Dems searched for another Dan Sullivan to confuse voters in Alaska race: bombshell report",
        outlet: "New York Post",
        summary:
          "A bombshell NYT report says Peltola's aides hunted for Alaskans named Dan Sullivan to siphon votes from the senator, and her ousted campaign manager says Peltola was 'definitely aware.'",
      },
      {
        headline:
          "Alaskan governor orders probe into alleged election scheme after NYT report",
        outlet: "Fox News",
        summary:
          "Gov. Mike Dunleavy ordered a state probe of an alleged Democrat-linked scheme by Peltola's team to recruit a same-name candidate, saying tricking voters 'strikes at the integrity of our elections.'",
      },
      {
        headline:
          "Full coverage: President Trump speaks at midterm rally in Wasilla",
        outlet: "Must Read Alaska",
        summary:
          "In a historic Mat-Su Valley visit, Trump endorsed Bernadette Wilson for governor and hailed Sullivan as 'the real Dan,' saying Democrats convinced Petersburg's Dan Sullivan to run.",
      },
    ],
    wire: [
      {
        headline:
          "Trump says he ordered Justice Department lawsuit over Alaska Senate race",
        outlet: "Associated Press",
        summary:
          "Trump said he directed the DOJ suit to block certification if Sen. Sullivan loses, citing a same-name rival on the ballot; Peltola called it 'flagrant, flagrant corruption.'",
      },
      {
        headline:
          "Alaska's governor orders a probe into Peltola's Senate campaign over spoiler candidate controversy",
        outlet: "Associated Press",
        summary:
          "Gov. Mike Dunleavy ordered an investigation after a Times report tied Peltola's campaign to recruiting another Dan Sullivan, a day after Trump urged prosecution; Peltola called it a 'distraction.'",
      },
      {
        headline:
          "Trump rallies for Alaska Republicans in Wasilla on Tuesday after endorsing Wilson in governor's race",
        outlet: "Anchorage Daily News",
        summary:
          "Trump endorsed Bernadette Wilson, whose business has been sued over unpaid bills, and called Bronson and Taylor 'lesser' candidates; Bronson disputed Trump's claim he'd agreed to quit.",
      },
    ],
  },
  AL: {
    left: [
      {
        headline:
          "Alabama Democrats see Trump's Mobile rally as a sign of GOP nerves",
        outlet: "MS NOW",
        summary:
          "Doug Jones and fellow Alabama Democrats cast Trump's Mobile rally as a sign of Republican worry, betting fuel prices, tariffs and anger over a new congressional map will energize voters.",
      },
      {
        headline: "MAGA Senator Goes on Crazed Posting Spree About Furries",
        outlet: "The New Republic",
        summary:
          "With weeks to go in his race for Alabama governor, Tommy Tuberville ignored voter concerns like soaring diesel prices to rant online that 'furries' have infiltrated the airline industry.",
      },
    ],
    right: [
      {
        headline:
          "Tuberville hits back at Jones for claiming he broke veterans salary promise — 'I want to see his bank returns also'",
        outlet: "1819 News",
        summary:
          "Tuberville said Doug Jones should 'do his homework' before claiming he broke his pledge to give his Senate salary to veterans, and said he wants to see Jones's bank returns.",
      },
      {
        headline:
          "Grand Old Podcast: Episode 9 – Barry Moore, Alabama's next U.S. Senator",
        outlet: "Yellowhammer News",
        summary:
          "On the state GOP chairman's podcast, Republican Senate nominee Barry Moore said America faces a 'spiritual battle' and that he is being positioned in the Senate to fight Sharia law.",
      },
    ],
    wire: [
      {
        headline:
          "Data centers in Alabama and how they might figure into the race for Alabama Governor",
        outlet: "Alabama Public Radio",
        summary:
          "Doug Jones hits Tommy Tuberville over past Alibaba stock trades, Tuberville blames Democrats and Chinese propaganda for data center opposition, and experts weigh water and power impacts.",
      },
      {
        headline:
          "Alabama Senate candidate says Trump's $5,000 offer 'might be a good investment'",
        outlet: "AL.com",
        summary:
          "Senate nominee Barry Moore backed Trump's proposed $5,000 checks if Republicans keep Congress as a 'good return on the investment'; another GOP congressman called the idea 'pay-to-play.'",
      },
    ],
  },
  AR: {
    left: [
      {
        headline: "The Return of the Farmer Politician",
        outlet: "The New Republic",
        summary:
          "Farmers battered by Trump's tariffs and the Iran war are running as Democrats, including Hallie Shoffner, who says closing her family farm drove her challenge to Tom Cotton.",
      },
      {
        headline:
          "In gubernatorial debate, Sanders relies on usual talking points as opponents attack her record",
        outlet: "Arkansas Times",
        summary:
          "Gov. Sarah Huckabee Sanders recycled rehearsed talking points touting her record, while Democrat Fred Love and Libertarian Colt Shelby accused her of ignoring Arkansans' real problems and taking undeserved credit.",
      },
    ],
    right: [
      {
        headline:
          "Red state Dem faces fury for suggesting illegal aliens should replace American workers",
        outlet: "Fox News",
        summary:
          "Democrat Hallie Shoffner faces scrutiny after saying on a podcast that farmers would 'trade five Americans' for one illegal immigrant worker, as Tom Cotton's campaign demanded answers.",
      },
      {
        headline:
          "'Insulting': Ex-Trump official's 'dishonest' campaign ad triggers conservative revolt",
        outlet: "Fox News",
        summary:
          "Gov. Sarah Huckabee Sanders drew conservative backlash for a reelection ad casting 'Brad,' a fictional young white man, as the face of welfare abuse; she doubled down.",
      },
    ],
    wire: [
      {
        headline:
          "With Cotton skipping debate, Shoffner and Wadlin make cases for U.S. Senate",
        outlet: "Arkansas Democrat-Gazette",
        summary:
          "With Sen. Tom Cotton skipping the Arkansas TV debate, Democrat Hallie Shoffner said he's either 'too good' or 'too scared,' as she and Libertarian Jeff Wadlin denounced the Iran war.",
      },
      {
        headline:
          "Sanders' challengers contend governor isn't concentrating enough on key issues Arkansans face",
        outlet: "Arkansas Democrat-Gazette",
        summary:
          "Gov. Sarah Huckabee Sanders defended her LEARNS education law as Democrat Fred Love vowed to end its vouchers by executive order and Libertarian Colt Shelby called it a failure.",
      },
    ],
  },
  AZ: {
    left: [
      {
        headline:
          "The Arizona governor's race is a fight over who certifies 2028",
        outlet: "MS NOW",
        summary:
          "Gov. Katie Hobbs is closing her campaign warning that Rep. Andy Biggs, who tried to overturn the 2020 election, would be the governor signing off on Arizona's 2028 presidential results.",
      },
      {
        headline: "Katie Hobbs goes after Andy Biggs at Phoenix rally",
        outlet: "The Copper Courier",
        summary:
          "At an Oct. 20 campaign event in Phoenix, Democratic Gov. Katie Hobbs slammed GOP opponent Rep. Andy Biggs, pointing to what she called his 'extreme' agenda in Congress.",
      },
    ],
    right: [
      {
        headline:
          "Trump torches Democrats at Arizona rally, warns they will 'destroy our country' ahead of midterms",
        outlet: "Fox News",
        summary:
          "At a Bullhead City rally for Rep. Andy Biggs, Trump warned Democrats would 'destroy our country,' attacked Gov. Katie Hobbs and urged a victory in honor of Charlie Kirk.",
      },
      {
        headline: "Gov. Katie Hobbs Yet To Sign Election Stability Pledge",
        outlet: "AZ Free News",
        summary:
          "Hobbs has declined to sign a cross-partisan pledge to follow election laws and accept certified results, even as Republican rival Andy Biggs and top Democrats like Kris Mayes signed it.",
      },
    ],
    wire: [
      {
        headline:
          "Republican Biggs debates No Labels' Hourihan as Gov. Hobbs skips Arizona governor race debate",
        outlet: "KJZZ",
        summary:
          "With Gov. Katie Hobbs absent, Andy Biggs and No Labels nominee Teri Ann Hourihan sparred over data centers, Medicaid and water; Biggs called Hobbs' refusal to debate 'disqualifying.'",
      },
      {
        headline:
          "Arizona's candidates for governor are fighting over who has the track record to lead the state",
        outlet: "KJZZ",
        summary:
          "Hobbs argues Biggs has a pattern of protecting abusers, citing his Epstein files vote and past bills, while Republicans brand her a 'veto queen' after a record 541 vetoes.",
      },
    ],
  },
  CA: {
    left: [
      {
        headline:
          "Steve Hilton, Xavier Becerra clash in testy California governor's race debate",
        outlet: "The Guardian",
        summary:
          "In a testy CNN debate, Becerra accused Trump-endorsed Hilton of owing loyalty to Trump and backing policies that would 'deport immigrants who look like me,' while Hilton called that 'disgraceful.'",
      },
      {
        headline:
          "Desperation plagues Republican Hilton's bid for California governor",
        outlet: "Daily Kos",
        summary:
          "Daily Kos casts Hilton's bid as marked by desperation, citing a widely panned innuendo-laden ad and his Trump ties, while Hilton pitches no state income tax on the first $150,000.",
      },
    ],
    right: [
      {
        headline:
          "Steve Hilton defeats Xavier Becerra in 'Mike Tyson' type of knockout debate: Joe Concha",
        outlet: "Washington Examiner",
        summary:
          "Washington Examiner columnist Joe Concha said Hilton delivered a knockout in the CNN debate, arguing 'Becerra had no answers whatsoever because Steve Hilton was dealing in facts.'",
      },
      {
        headline:
          "Xavier Becerra Backs Out of Another Debate with Steve Hilton",
        outlet: "California Globe",
        summary:
          "The Globe says Becerra, 'mostly running his campaign from an underground bunker,' backed out of a Univision debate after skipping an LA Times one, as Hilton accused him of hiding.",
      },
    ],
    wire: [
      {
        headline:
          "Rivals for California governor debate over immigration and Trump as ballots go to voters",
        outlet: "Associated Press",
        summary:
          "In their only televised debate, Republican Steve Hilton cast himself as a change agent after years of Democratic dominance, while Democrat Xavier Becerra tied him to Trump.",
      },
      {
        headline:
          "Becerra's muted gubernatorial campaign comes to life in homestretch",
        outlet: "Los Angeles Times",
        summary:
          "After months largely absent from the trail, Democrat Xavier Becerra has stepped up appearances and TV ads as ballots arrive, while Steve Hilton blasts him for taking voters for granted.",
      },
    ],
  },
  CO: {
    left: [
      {
        headline: "MAGA Candidate Brags He Physically Abused His Wife",
        outlet: "The New Republic",
        summary:
          "Colorado's MAGA-aligned GOP nominee Victor Marx told donors he'd shove his wife against a door again, claiming she was 'fake-crying,' and has also been accused of throwing his toddler.",
      },
      {
        headline: 'Do No Harm? "Hold My Beer," Says Mark Baisley',
        outlet: "Colorado Pols",
        summary:
          "GOP Senate nominee Mark Baisley's debate defenses of a national abortion ban, Medicaid cuts and the Iran war showed voters why Colorado Republicans shouldn't be near the levers of power.",
      },
    ],
    right: [
      {
        headline:
          "Election office says there's enough evidence to investigate Weiser",
        outlet: "The Denver Gazette",
        summary:
          "State election officials found enough evidence to probe Democrat Phil Weiser's campaign over 46 over-limit donations refunded late; Marx's campaign said Weiser was sidestepping his own conduct.",
      },
      {
        headline:
          "Hickenlooper, Baisley compete in Colorado's U.S. Senate race",
        outlet: "The Center Square",
        summary:
          "With Colorado ranked near the bottom for affordability, Hickenlooper blames Trump's agenda, while Republican Mark Baisley vows to cut spending and take regulation away from 'unelected bureaucrats.'",
      },
    ],
    wire: [
      {
        headline:
          "Colorado Governor candidates Victor Marx and Phil Weiser share stage for a non-debate",
        outlet: "Colorado Public Radio",
        summary:
          "Weiser and Marx shared a stage once, at a Chamber forum with questions provided in advance; Marx, who has declined all moderated debates, read his answers from a folder.",
      },
      {
        headline:
          "In Senate race, John Hickenlooper and Mark Baisley debate on Trump, abortion bans and Iran",
        outlet: "The Denver Post",
        summary:
          "Hickenlooper tied Baisley to Trump and regretted backing Trump's Cabinet picks; Baisley defended the Iran war, trusted Trump's Russian diesel deal and backed a national abortion ban.",
      },
    ],
  },
  CT: {
    left: [],
    right: [
      {
        headline:
          "Lamont Picks Chinese Communist Zhou Enlai For Fantasy Dinner In Debate; Fazio Chooses Lincoln, Jesus, And Babe Ruth",
        outlet: "Connecticut Centinal",
        summary:
          "Asked to name dream dinner guests in the first debate, Lamont chose Communist Chinese premier Zhou Enlai while Fazio picked Lincoln, Jesus and Babe Ruth, 'a window into priorities.'",
      },
      {
        headline:
          "Fazio Hits Lamont On Failure To Provide Relief At Gas Pump With Tax Holiday",
        outlet: "Connecticut Centinal",
        summary:
          "Fazio pressed Lamont over opposing a 25-cent gas tax holiday he backed four years ago, noting a transportation fund surplus could pay for it; Lamont veered into criticizing Trump.",
      },
    ],
    wire: [
      {
        headline:
          "Lamont, Fazio lively in last debate while breaking no new ground",
        outlet: "Connecticut Public",
        summary:
          "In their final debate, Lamont touted steering Connecticut to fiscal stability and faulted Republicans for not opposing Trump, while Fazio, the aggressor, hammered state mandates and property taxes.",
      },
      {
        headline:
          "Lamont faces criticism over 'cozy' relationship with Eversource CEO",
        outlet: "Connecticut Public",
        summary:
          "Lamont's tough talk on Eversource was undercut by its CEO describing governors as friends and texts urging him to intervene on storm costs; Lamont called their relationship civil.",
      },
    ],
  },
  DE: {
    left: [
      {
        headline: "DL Open Thread: Saturday, September 19, 2026",
        outlet: "Delaware Liberal",
        summary:
          "Mocking Delaware Republicans, the post spotlights the party's revolt after Senate nominee Mike Katz urged House nominee Joe Arminio to quit for misgendering Rep. Sarah McBride.",
      },
    ],
    right: [
      {
        headline:
          "Former Democrat wins GOP US Senate nod in deep-blue Delaware",
        outlet: "Fox News",
        summary:
          "Ex-Democratic state senator Mike Katz won Delaware's GOP Senate primary, pitching a coalition of Republicans, independents and Democrats to unseat Chris Coons in a seat Republicans haven't won since 1994.",
      },
    ],
    wire: [
      {
        headline: "US Senate candidates Coons and Katz face off in forum",
        outlet: "Delaware Public Media",
        summary:
          "At a Wilmington forum, Coons swiped at Trump's Iran war and Medicaid cuts, while Katz called the Affordable Care Act a failed policy and repeatedly demanded a formal debate.",
      },
      {
        headline:
          "Delaware Republican U.S. Senate candidate alleges campaign interference by 'powerful elite' during forum",
        outlet: "WHYY",
        summary:
          "Katz said Delaware had become 'unaffordable and is dying' and alleged a 'powerful elite' hacked and threatened his campaign; Coons urged voters not to risk an 'untested candidate.'",
      },
    ],
  },
  FL: {
    left: [
      {
        headline: "AOC makes 'common sense' pitch for Florida's Angie Nixon",
        outlet: "Daily Kos",
        summary:
          "Campaigning in Kissimmee, Rep. Alexandria Ocasio-Cortez said good schools, funded hospitals and guaranteed healthcare are 'not communism—it's common sense,' touting Nixon as the only Senate candidate refusing corporate money.",
      },
      {
        headline:
          "Trump fumes after Byron Donalds scrubbed his campaign website in Florida",
        outlet: "MS NOW",
        summary:
          "Sycophantic Trump loyalist Byron Donalds scrubbed Trump's endorsement and photo from his campaign site, and the president answered with a 459-word tirade calling the accurate reports 'fake.'",
      },
    ],
    right: [
      {
        headline:
          "Angie Nixon struggles to say if she's capitalist or socialist on Politico",
        outlet: "Fox News",
        summary:
          "Pressed on her Democratic Socialists of America membership, Angie Nixon said 'I don't know' whether she's capitalist or socialist; Moody's campaign said her 'true colors' were showing.",
      },
      {
        headline:
          "Resurfaced Video Of David Jolly's Support for Cuban Consulate Sparks Concerns",
        outlet: "The Floridian",
        summary:
          "A resurfaced 2016 clip shows then-Republican Rep. David Jolly backing a Cuban consulate in St. Petersburg after meeting Cuban officials, raising questions about his stance on Cuba's communist regime.",
      },
    ],
    wire: [
      {
        headline:
          "Angie Nixon keeps hammering on Hope Florida. What happened? What was Ashley Moody's involvement?",
        outlet: "WLRN Public Media",
        summary:
          "Nixon accuses Moody of stealing $10 million meant for sick kids via Hope Florida; Moody's office approved the settlement, but her role is unclear and no one has been charged.",
      },
      {
        headline:
          "David Jolly says debates 'further toxicity.' Other Democrats disagree",
        outlet: "Miami Herald",
        summary:
          "Democrat David Jolly called his refusal to debate Byron Donalds final, citing bad faith in politics, while Donalds called it 'silly' and few Democrats on the ticket backed Jolly's stance.",
      },
    ],
  },
  GA: {
    left: [
      {
        headline:
          "Jon Ossoff Shreds GOP Opponent For Hiring 'Half The Third Reich' In Blistering Debate",
        outlet: "HuffPost",
        summary:
          "Ossoff confronted Collins over his son-in-law's neo-Nazi posts and aides' extremist ties, accusing him of hiring 'half the Third Reich' and declaring flatly, 'Mike Collins is a bigot.'",
      },
      {
        headline:
          "Jon Ossoff's rise to US Senate was meteoric – how high can he go?",
        outlet: "The Guardian",
        summary:
          "Media-savvy Ossoff, whose viral jabs at Trump and 'the Epstein class' fuel 2028 buzz, insists he has 'zero interest' while putting Collins on trial over white-nationalist ties.",
      },
      {
        headline: "Bottoms, Jackson face off in only scheduled debate",
        outlet: "Courier Georgia",
        summary:
          "At their only debate, Jackson softened his Medicaid and Trump stances and denied a reported National Guard remark, while Bottoms doubled down on Medicaid expansion and pausing data centers.",
      },
    ],
    right: [
      {
        headline:
          "Mike Collins fends off claims he's a 'bigot,' rips 'trust fund kid' Jon Ossoff in Georgia Senate debate",
        outlet: "New York Post",
        summary:
          "Collins brushed off Ossoff's 'bigot' charges and ripped the 'trust fund kid' senator for taking Epstein-linked Reid Hoffman's money and shutting down the government to defund ICE.",
      },
      {
        headline:
          "Trump blasts Ossoff as 'far left extremist' as he touts new Ga. jobs at rally",
        outlet: "New York Post",
        summary:
          "Stumping in Perry for Collins, Trump branded Ossoff a 'far-left extremist' who 'votes to destroy everything Georgia values' while touting a $4.2 billion Audi plant bringing thousands of jobs.",
      },
      {
        headline:
          "GIANNO CALDWELL: Bottoms ran Atlanta into the ground. We can't let her do it to Georgia",
        outlet: "Fox News",
        summary:
          "Gianno Caldwell's op-ed says Bottoms ran Atlanta into the ground as homicides soared and officers quit, while Rick Jackson's foster-care-to-CEO story and crime plan make him the better choice.",
      },
    ],
    wire: [
      {
        headline: "Takeaways from Tuesday's U.S. Senate debate",
        outlet: "Atlanta Journal-Constitution",
        summary:
          "In an hourlong slugfest, Collins came out swinging after a defensive first debate, calling Ossoff a 'globe-trotting, trust fund kid' as they clashed over Reid Hoffman, abortion and immigration.",
      },
      {
        headline: "Trump takes his midterm blitz to swing state of Georgia",
        outlet: "Associated Press",
        summary:
          "In Dalton, Trump rallied with Collins and Jackson, urged supporters to 'pretend I'm on the ballot,' and repeated false claims that Ossoff won his seat in a 'rigged election.'",
      },
      {
        headline:
          "Republican billionaire Rick Jackson is walking a Trump tightrope in his quest to lead Georgia",
        outlet: "Associated Press",
        summary:
          "Billionaire Jackson casts himself as a Trump-style working-class champion, while Bottoms says he's 'trying to buy this election' and Democrats seize on his 'my plumber drives a Mercedes' remark.",
      },
    ],
  },
  HI: {
    left: [
      {
        headline:
          "This Farmer-Artist Questions Hawaiʻi’s Statehood. He Wants To Be LG",
        outlet: "Honolulu Civil Beat",
        summary:
          "Republican lieutenant governor nominee Daniel Anthony, Gary Cordery's running mate, questions the legitimacy of Hawaiʻi statehood and has posted about rigged elections and hurricane 'weather manipulation.'",
      },
    ],
    right: [
      {
        headline:
          "Conspiracy in Common: How Cordery Campaign Brings Sovereignty Activists into Hawaii Republican Party",
        outlet: "Hawaii Free Press",
        summary:
          "Excerpting Civil Beat, the site mocks GOP running mate Daniel Anthony's statehood skepticism and 'weather manipulation' posts, casting Cordery as importing sovereignty activists into the Republican Party.",
      },
    ],
    wire: [
      {
        headline:
          "Audit slams ‘questionable’ spending for Hawaii’s pricey kauhale initiative",
        outlet: "Honolulu Star-Advertiser",
        summary:
          "A state audit flagged $13.7 million in unauthorized or questionable payments in Gov. Josh Green's signature kauhale homeless program, a reelection pillar; Green says the villages are working.",
      },
      {
        headline:
          "Republican ticket brings diverse approaches to gubernatorial race",
        outlet: "Honolulu Star-Advertiser",
        summary:
          "GOP running mates Gary Cordery and Daniel Anthony pitch streamlined permitting and local food production; Cordery calls Green's emergency-driven kauhale effort 'a Band-Aid, not a policy.'",
      },
    ],
  },
  IA: {
    left: [
      {
        headline:
          "Josh Turek, Ashley Hinson spar over wealth, missed votes, and Iran in final Iowa Senate debate",
        outlet: "Iowa Starting Line",
        summary:
          "Hinson, who campaigns on banning congressional stock trading, acknowledged her husband's private stock holdings as Turek accused her of enriching herself; she countered by hitting his missed votes.",
      },
      {
        headline:
          "The Iowa governor’s race between Rob Sand and Zach Lahn is the future of American politics.",
        outlet: "Slate",
        summary:
          "Slate casts the millennial Sand–Lahn contest as a preview of post-Trump politics, pitting Sand's anti-party disdain against Lahn's fusion of Trumpist populism and older conservatism.",
      },
    ],
    right: [
      {
        headline:
          "Iowa Dem Senate candidate says he's too busy campaigning to attend all state House votes",
        outlet: "Fox News",
        summary:
          "Hinson hammered Turek for showing up to only 42% of Iowa House votes this year, a record Fox News Digital first uncovered; Turek said he was campaigning statewide.",
      },
      {
        headline:
          "F-150s, Family Fortunes And Kansas Residency: Iowa Governor Debate Becomes A Battle Over Authenticity",
        outlet: "Daily Caller News Foundation",
        summary:
          "Lahn repeatedly branded Sand a 'liberal billionaire' via his wife's family company and called Sand's Kansas-residency attacks 'a central lie' of his campaign.",
      },
    ],
    wire: [
      {
        headline:
          "U.S. Senate candidates discuss Iran war, affordability and defend voting records in debate",
        outlet: "Iowa Public Radio",
        summary:
          "At their Iowa PBS debate, Hinson defended opposing an Iran war powers resolution, Turek vowed to fight to end the war, and both touted bipartisanship on immigration.",
      },
      {
        headline:
          "Iowa faces a reckoning over water quality and cancer rates in midterm campaigns",
        outlet: "Associated Press",
        summary:
          "Water quality and cancer rates have been forced to the forefront of Iowa's campaigns; governor hopefuls Zach Lahn and Rob Sand prioritize water, but neither backs mandatory farm runoff rules.",
      },
    ],
  },
  ID: {
    left: [
      {
        headline:
          "Independent candidates could swing the Senate. What would they actually do there?",
        outlet: "Vox",
        summary:
          "Red-state independents for whom Democrats cleared the field, including Idaho's Todd Achilles against 83-year-old Jim Risch, envision a 'Fulcrum Caucus' that would deny either party a Senate majority.",
      },
      {
        headline:
          "Pickens, Stegner pledge policy changes in Idaho gubernatorial candidate forum",
        outlet: "Idaho Capital Sun",
        summary:
          "Democrat Terri Pickens and independent John Stegner vowed to reverse Gov. Brad Little's budget cuts and school vouchers at a forum Little skipped, leaving his chair empty.",
      },
    ],
    right: [
      {
        headline: "Sen. Risch Reacts to Achilles Airport Encounter",
        outlet: "Newstalk 107.9",
        summary:
          "Risch said he walked away when Achilles approached him at the airport, citing the challenger's negative attacks, reaffirmed he won't debate, and called the independent an 'extreme leftist.'",
      },
      {
        headline:
          "Gov Hopeful Terri Pickens on Prop 1, Parental Choice, School Choice",
        outlet: "Newstalk 107.9",
        summary:
          "Democrat Terri Pickens vowed to repeal Idaho's $50 million parental choice tax credit, backed Prop 1's Roe standard, and dismissed Stegner's suggestion that she might quit the race.",
      },
    ],
    wire: [
      {
        headline:
          "Facing relentless challenger, Idaho’s Sen. Jim Risch makes a case for fourth term",
        outlet: "Idaho Statesman",
        summary:
          "Risch, 83, touts his conservative record and Trump's endorsement and calls debates not 'indispensable,' while independent Todd Achilles hammers him as inaccessible, down to 'Debate Me Bro' signs.",
      },
      {
        headline:
          "Fact check: Where Idaho governor candidates stand on immigration",
        outlet: "Idaho Statesman",
        summary:
          "In a state where immigration enforcement tests who is a 'real' Republican, the Statesman finds Little touting ICE partnerships while Pickens and Stegner pair targeting criminals with legal pathways.",
      },
    ],
  },
  IL: {
    left: [
      {
        headline:
          "The Black women holding each other up on the path to the Senate",
        outlet: "The 19th",
        summary:
          "Illinois' Juliana Stratton, seeking to join Sens. Alsobrooks and Blunt Rochester as Black women serving together, describes their mentorship and the joy she keeps despite racist and sexist criticism.",
      },
      {
        headline:
          "A billionaire is one of Trump’s loudest critics. Will Democrats send him to the White House?",
        outlet: "The Guardian",
        summary:
          "Profiling JB Pritzker as self-appointed captain of 'Team Fight' against Trump, the Guardian weighs his 2028 prospects as he first seeks a third term against Republican Darren Bailey.",
      },
    ],
    right: [
      {
        headline: "Stratton, Tracy offer opposing pitches for Senate race",
        outlet: "The Center Square",
        summary:
          "Tracy blamed the 'Pritzker-Stratton administration' for nearly 70 tax and fee hikes and called Stratton's $25 minimum wage a small-business 'death sentence,' while she faulted Trump's war and tariffs.",
      },
      {
        headline: "Pritzker, Bailey trade jabs in race for governor",
        outlet: "The Center Square",
        summary:
          "At their lone forum, Bailey decried nearly $200 billion in pension debt, called taxing the rich 'socialist' and urged repealing the SAFE-T Act, while Pritzker defended a millionaires tax.",
      },
    ],
    wire: [
      {
        headline:
          "Key takeaways from the only Senate debate between Juliana Stratton and Don Tracy",
        outlet: "WBEZ Chicago",
        summary:
          "Stratton repeatedly vowed to stand up to Trump and defended a $25 minimum wage and abolishing ICE, while Tracy called her a socialist three times and warned of small-business harm.",
      },
      {
        headline:
          "Pritzker, Bailey Square Off in Contentious Discussion Over Taxes, Trump and Bears",
        outlet: "WTTW News",
        summary:
          "In their only meeting, Pritzker and Bailey talked over each other and traded accusations of lying, as Bailey faulted Pritzker's supermajority record and Pritzker repeatedly tied Bailey to Trump.",
      },
    ],
  },
  KS: {
    left: [
      {
        headline:
          "ADL condemns Trump's antisemitic slur but fails to mention him by name",
        outlet: "The Guardian",
        summary:
          "Trump used an antisemitic slur at a Kansas rally defending Sen. Roger Marshall's lawsuits against hundreds of patients; the Anti-Defamation League condemned the term without naming him.",
      },
      {
        headline:
          "“Demonic Stuff”: Kansas GOP Accuses Democrat of Being a Witch",
        outlet: "The New Republic",
        summary:
          "Republican Ty Masterson told a Wichita church that his race is a 'spiritual battle,' accusing Cindy Holscher's camp of 'witchcraft spells' over her daughter's playful TikTok videos.",
      },
    ],
    right: [
      {
        headline:
          "Woke megachurch pastor, Dem nominee called on congregation to repent for driving, eating beef",
        outlet: "Fox News",
        summary:
          "In Kansas, where cows are king, Democrat Adam Hamilton once urged his congregation to repent for driving and eating beef, in sermons Marshall calls 'woke' and counter to Kansans' values.",
      },
      {
        headline:
          "Kansas Democrat Cindy Holscher Says She Isn't a Socialist, but Campaigns with One the Next Day",
        outlet: "Breitbart News",
        summary:
          "A day after declaring 'I am no socialist,' Democrat Cindy Holscher campaigned with a self-described Democratic Socialist at a Democratic Socialists of America event, a contradiction Republicans seized on.",
      },
    ],
    wire: [
      {
        headline:
          "Kansas Sen. Roger Marshall says the Ukraine war, not the Iran war, has caused high diesel prices",
        outlet: "Associated Press",
        summary:
          "In their final debate, Sen. Roger Marshall blamed the Ukraine war, not the Iran war, for high diesel prices, echoing Trump; Adam Hamilton said Marshall would 'sell out' Ukraine.",
      },
      {
        headline:
          "Kansas governor election: What to know about the candidates running in November",
        outlet: "KCUR (Kansas News Service)",
        summary:
          "Trump-endorsed Ty Masterson has toned down his MAGA messaging and populist Democrat Cindy Holscher is pivoting to the middle as both court unaffiliated voters, calling each other extreme.",
      },
    ],
  },
  KY: {
    left: [
      {
        headline: "Booker brings in the pros for debate prep",
        outlet: "Forward Kentucky",
        summary:
          "Charles Booker prepped for his only debate with former Biden aides, who say Andy Barr spurned other debates to dodge his record and constantly 'fearmongers' about Booker.",
      },
      {
        headline:
          "Andy Barr is coming to save us from socialism. Or so he says.",
        outlet: "Forward Kentucky",
        summary:
          "Columnist Bill Straub mocks Andy Barr's warning that losing Kentucky's seat means losing 'our country,' calling his socialism scare over Charles Booker silly while he ignores extremists in his party.",
      },
    ],
    right: [
      {
        headline: "Trump rallies Bluegrass voters to support GOP ticket",
        outlet: "The Washington Times",
        summary:
          "At a Northern Kentucky rally, Trump urged supporters to elect his 'friend' Andy Barr, an 'amazing congressman,' and 'defeat the radical left Democrat Charles Booker.'",
      },
      {
        headline:
          "GOP Senate candidate predicts voters will 'reject the Mamdani of Kentucky'",
        outlet: "Fox News",
        summary:
          "Rep. Andy Barr told Fox's 'The Sunday Briefing' that Charles Booker's support for abolishing ICE is dangerous, attacking his progressive immigration and health care stances ahead of November.",
      },
    ],
    wire: [
      {
        headline:
          "Barr and Booker clash over economy, war and immigration in KY Senate debate",
        outlet: "Lexington Herald-Leader",
        summary:
          "In their only debate, Andy Barr called Charles Booker a 'crazy socialist' akin to Zohran Mamdani, while Booker blamed rising prices on Trump's tariffs and Iran war, which Barr backs.",
      },
      {
        headline:
          "Election 2026: Andy Barr, Charles Booker compete in U.S. Senate race",
        outlet: "Kentucky Public Radio",
        summary:
          "Trump-endorsed Rep. Andy Barr, who voted for Trump's signature tax-and-spending bill, faces progressive Charles Booker, on his third Senate run, for the seat Mitch McConnell held over four decades.",
      },
    ],
  },
  LA: {
    left: [
      {
        headline:
          "Jamie Davis attacks Julia Letlow over Meta stock scandal in new U.S. Senate ad",
        outlet: "Gambit",
        summary:
          "Jamie Davis's new ad hits Julia Letlow for buying Meta and Nvidia stock after signing a secret Meta NDA and disclosing late; Davis vows never to trade stock in office.",
      },
    ],
    right: [
      {
        headline: "Yes, Go And Vote For Julia Letlow. Don't Be An Ass.",
        outlet: "The Hayride",
        summary:
          "Despite a lackluster Letlow campaign, Republicans tempted to back Jamie Davis should remember Pete Buttigieg stumped for him and Davis would vote with national Democrats every time.",
      },
      {
        headline:
          "Senate candidate Jamie Davis calls for data center moratorium",
        outlet: "The Hayride (republishing The Center Square)",
        summary:
          "Democrat Jamie Davis wants new Louisiana data centers halted over aquifer water use, even while praising Meta, which promises closed-loop cooling and $340 million for local water.",
      },
    ],
    wire: [
      {
        headline:
          "As early voting ends in Louisiana, Senate candidates make final pitches: 'go to the polls'",
        outlet: "The Advocate",
        summary:
          "On early voting's last day, Jamie Davis rallied in New Orleans with Pete Buttigieg and a brass band while Julia Letlow wound down a 64-parish tour of closed-door events.",
      },
      {
        headline:
          "How tragedy, faith led Julia Letlow to run for Senate: 'I needed those signs from the Lord'",
        outlet: "The Times-Picayune",
        summary:
          "Profile traces how Julia Letlow, widowed when her husband died of COVID weeks after his House win, ran in his place, guided by faith, and now seeks a Senate seat.",
      },
    ],
  },
  MA: {
    left: [
      {
        headline:
          "After running mate's arrest, Mass. GOP gubernatorial nominee says he will govern alone if elected",
        outlet: "MS NOW",
        summary:
          "Republican Mike Minogue says he'd govern without a lieutenant governor after running mate Shawn Oliver's arrest on suspicion of drunken driving, while urging voters to still pick the Minogue-Oliver ticket.",
      },
      {
        headline: "Democratic Voices, Spotlight: Governor Maura Healey",
        outlet: "Daily Kos",
        summary:
          "A Daily Kos community spotlight celebrates Gov. Maura Healey's toughness and Bluesky messaging, noting Massachusetts has elected Republican governors before but quipping the GOP 'used to nominate some sane candidates.'",
      },
    ],
    right: [
      {
        headline: "Ed Markey Backs Out of Oct. 14 Debate Against John Deaton",
        outlet: "New Boston Post",
        summary:
          "Sen. Ed Markey backed out of an Oct. 14 WCVB debate against Republican challenger John Deaton, according to MassGOP Chair Amy Carnevale.",
      },
      {
        headline:
          "Healey, Minogue and dueling crowds clash at first governor's debate",
        outlet: "Boston Herald",
        summary:
          "Gov. Healey invoked Trump 32 times, casting Minogue as a MAGA 'rubber stamp,' while Minogue hammered her record on energy costs, jobs and spending; she dodged naming any mistakes.",
      },
    ],
    wire: [
      {
        headline:
          "Ed Markey, John Deaton met in their only US Senate debate. Here's what we learned.",
        outlet: "The Boston Globe",
        summary:
          "Markey named Trump over three dozen times to tie Deaton to the president, while Deaton attacked Markey's decades in Washington and questioned whether the 80-year-old could serve another term.",
      },
      {
        headline:
          "Healey and Minogue meet in unbridled final debate before election",
        outlet: "WBUR",
        summary:
          "Healey and Minogue savaged each other's positions in their last debate, sparring over affordability, Trump, abortion and a former Healey aide charged with distributing cocaine.",
      },
    ],
  },
  MD: {
    left: [
      {
        headline:
          "Do Maryland's political leaders serve Maryland? Post-gubernatorial debate town hall",
        outlet: "The Real News Network",
        summary:
          "A Baltimore panel called the Moore-Cox debate a 'missed opportunity' for bold ideas, faulting both parties' safe politics-as-usual and urging grassroots governing power beyond the two-party system.",
      },
      {
        headline:
          "Moore, Cox clash over Maryland's budget, immigration and public safety in gubernatorial debate",
        outlet: "The AFRO-American Newspapers",
        summary:
          "In their debate, Gov. Wes Moore called Dan Cox's claims 'factually incorrect,' touted Baltimore's drop in homicides and defended limiting police cooperation with ICE, while Cox vowed no new taxes.",
      },
    ],
    right: [
      {
        headline:
          "Wes Moore's 2028 dodge complicates reelection pitch in Maryland",
        outlet: "Washington Examiner",
        summary:
          "Gov. Wes Moore's debate refusal to rule out leaving office early is supercharging 2028 speculation and fueling Republican charges he cares more about national ambitions than Maryland.",
      },
      {
        headline:
          "Dan Cox slams governor's support for Question 3 at state house",
        outlet: "FOX45 News Baltimore (WBFF)",
        summary:
          "Dan Cox blamed Wes Moore for high electric bills and taxes, called the Question 3 redistricting amendment a 'disgrace' for a governor 'running for president,' and pledged a full term.",
      },
    ],
    wire: [
      {
        headline:
          "Evaded questions and 'political TMZ:' takeaways from Maryland's gubernatorial debate",
        outlet: "WYPR (Baltimore public radio)",
        summary:
          "Moore and Cox offered few plans: Moore stayed vague on closing a $3 billion deficit and serving a full term, while Cox called questions about January 6 'political TMZ.'",
      },
      {
        headline:
          "Dan Cox wants to talk about Maryland but can't escape Trump questions",
        outlet: "The Baltimore Banner",
        summary:
          "Dan Cox now pivots from Trump to affordability, the Key Bridge and a data center ban, but Democrats keep spotlighting his election denial and Jan. 6 buses.",
      },
    ],
  },
  ME: {
    left: [
      {
        headline:
          "Collins struggles to cite consequential vote against Trump in final debate with Jackson",
        outlet: "Maine Beacon",
        summary:
          "Asked when defying Trump changed an outcome, Collins cited opposing Hegseth, who was confirmed anyway; Jackson said she's there '100% of the time when Donald Trump needs her.'",
      },
      {
        headline:
          "Troy Jackson makes final pitch to Maine voters in seven-figure ad blitz",
        outlet: "MS NOW",
        summary:
          "Jackson's closing ad casts the race as billionaires and Trump versus everyday Mainers, as Trump moved to lift Canadian tariffs partly to bolster Collins' reelection.",
      },
      {
        headline:
          "Climate Change Is on the Ballot—Even If Dems Aren't Saying Those Words",
        outlet: "The New Republic",
        summary:
          "In Maine's governor race, Hannah Pingree blamed soaring energy costs partly on Trump's Iran war and championed heat pumps and efficiency—a model for Democrats linking climate to pocketbooks.",
      },
    ],
    right: [
      {
        headline:
          "Susan Collins and Troy Jackson clash over ICE in Maine Senate debate",
        outlet: "Fox News",
        summary:
          "Jackson said Collins 'paid for the bullets' in a fatal ICE shooting and, like the DSA, wants ICE abolished; Collins argued for reform, noting ICE fights child trafficking.",
      },
      {
        headline:
          "'OH, SHUT UP!' Troy Jackson Snaps at Susan Collins in Explosive Final Maine Senate Debate",
        outlet: "The Maine Wire",
        summary:
          "Jackson told Collins to 'shut up' after she tied a debate protester to him, an outburst The Maine Wire linked to his short fuse and a 1988 assault incident.",
      },
      {
        headline:
          "Charles Demands Financial Records Tied to Pingree's Husband; Democrat Calls Allegations 'Completely Made Up'",
        outlet: "The Maine Wire",
        summary:
          "Bobby Charles demanded contracts and invoices to show whether state grants awarded on Hannah Pingree's watch reached her husband's film company; Pingree and a grantee deny any public money did.",
      },
    ],
    wire: [
      {
        headline:
          "Last Republican incumbent senator in a Democratic-leaning state fights for survival",
        outlet: "Reuters",
        summary:
          "Collins touts nearly $1.5 billion in earmarks and tariff carve-outs as Jackson, a former logger, brands her a Trump rubber stamp, while strategists say persuadable voters have shrunk.",
      },
      {
        headline:
          "Troy Jackson tells Susan Collins to 'shut up' during heated Maine Senate debate",
        outlet: "Associated Press",
        summary:
          "Jackson told Collins to 'shut up' after she called a data center protester his supporter, in a final debate that also featured clashes over the debt and Trump.",
      },
      {
        headline:
          "Bobby Charles levels unfounded corruption claims at Hannah Pingree in final debate",
        outlet: "Bangor Daily News",
        summary:
          "Charles accused Pingree, without evidence, of using her state job to enrich her family; public records don't back him, and independent Rick Bennett struggled to get words in.",
      },
    ],
  },
  MI: {
    left: [
      {
        headline:
          "How Laura Loomer and Trump allies amplified 'Muslim Brotherhood' lie dogging Abdul El-Sayed",
        outlet: "The Guardian",
        summary:
          "Republicans' Muslim Brotherhood attacks on El-Sayed rest on a false Laura Loomer claim about a 2012 pro-democracy letter that never mentions the group, a Guardian review found.",
      },
      {
        headline:
          "Mike Rogers Pulls The 'Your Mom' Card On El-Sayed In Michigan Senate Debate",
        outlet: "HuffPost",
        summary:
          "Rogers went after El-Sayed's mother and revived debunked Muslim Brotherhood claims, but an unfazed El-Sayed said his rival cared more about attacking him than about voters' concerns.",
      },
      {
        headline:
          "Barack Obama says Jocelyn Benson has 'real answers' for Michigan families",
        outlet: "The Gander",
        summary:
          "In a new video, Obama urged Michiganders to elect Benson, saying she 'proved that government can solve big problems' and has 'real answers' on healthcare costs, housing and schools.",
      },
    ],
    right: [
      {
        headline:
          "Vance tears into 'psychopath' Abdul El-Sayed, blasts Dem for peddling 'anti-white' hatred",
        outlet: "New York Post",
        summary:
          "Campaigning for Rogers, Vance called El-Sayed 'a little bit of a psychopath' who traffics in 'anti-white racial hatred,' lied about being an immigrant and would kill Michigan's auto industry.",
      },
      {
        headline:
          "Michigan Senate debate erupts into personal attacks as Rogers, El-Sayed clash over Israel, Iran and more",
        outlet: "Fox News",
        summary:
          "Rogers branded El-Sayed a 'con man' who lied about being a physician, while El-Sayed wouldn't say whether Israel or Iran poses the bigger threat and floated oil price controls.",
      },
      {
        headline:
          "Jocelyn Benson promises tax hikes if elected governor - seeks commission to 'help us find more revenue'",
        outlet: "The Midwesterner",
        summary:
          "Benson, who is overseeing her own election, signaled tax hikes by vowing a blue-ribbon commission to 'find more revenue,' after once admitting she didn't know Michigan's income tax rate.",
      },
    ],
    wire: [
      {
        headline:
          "Democratic divisions linger in Michigan Senate race. Will frustration with Trump outweigh them?",
        outlet: "Associated Press",
        summary:
          "Days before the vote, Democrats remain split over El-Sayed after a bruising primary, with Haley Stevens' fiancé backing Rogers and some Jewish voters uneasy over his Israel criticism.",
      },
      {
        headline:
          "Michigan Senate race a test of attacks on Muslim candidate, progressive agenda",
        outlet: "Reuters",
        summary:
          "Michigan's race tests whether identity attacks portraying El-Sayed, who'd be the first Muslim senator, as sympathetic to Islamist extremism can succeed, and whether Democrats will embrace his progressive agenda.",
      },
      {
        headline:
          "Benson, James stick to campaign message in debate with no clear winner",
        outlet: "Detroit Free Press",
        summary:
          "Benson and James stuck to their messages in a first debate with no clear winner, splitting on literacy, clean energy and income taxes and trading barbs over data centers.",
      },
    ],
  },
  MN: {
    left: [
      {
        headline:
          "How Peggy Flanagan navigates a thorny issue in her Senate race",
        outlet: "Daily Kos",
        summary:
          "Flanagan tells Daily Kos that Walz owned the fraud response, calls ICE 'lawless' amid racist GOP scapegoating of Somalis, and vows to fight fraud and corruption in Trump's administration.",
      },
      {
        headline:
          "When asked about Renee Good and Alex Pretti, Lisa Demuth pivots to blaming Gov. Walz",
        outlet: "Courier Minnesota",
        summary:
          "Asked about Renée Good and Alex Pretti's deaths during Minneapolis ICE operations, Lisa Demuth brushed it off, blaming Gov. Walz and a lack of cooperation with federal authorities.",
      },
    ],
    right: [
      {
        headline:
          "'Buck' stops at Walz: Flanagan ducks Minnesota fraud blame as GOP rival exposes glaring contradiction",
        outlet: "Fox News",
        summary:
          "Tafoya pressed Flanagan over up to $9 billion in fraud during her eight years as Walz's second-in-command; Flanagan pointed to Walz, and Tafoya called the deflection unconvincing.",
      },
      {
        headline:
          "Republicans say Walz attempting to undermine new anti-fraud office, Demuth calls on Klobuchar to act",
        outlet: "Alpha News",
        summary:
          "GOP state senators accuse lame-duck Gov. Walz of undermining the new anti-fraud inspector general office, and Lisa Demuth urges Klobuchar to rein in her 'top ally.'",
      },
    ],
    wire: [
      {
        headline:
          "Trump inserts himself in Minnesota's Senate race with unfounded fraud warning",
        outlet: "Minnesota Star Tribune",
        summary:
          "Trump touted Tafoya and claimed without evidence she can lose only if 'they cheat' with ballots, warning the federal government will be watching; Flanagan mockingly congratulated Tafoya.",
      },
      {
        headline:
          "Klobuchar, Demuth tangle over what they've done in politics, what they would do as governor",
        outlet: "MPR News",
        summary:
          "In a lively final debate, Klobuchar cast Demuth as a Trump 'rubber stamp' backed by a Wisconsin billionaire, while Demuth painted Klobuchar as a continuation of Gov. Tim Walz.",
      },
    ],
  },
  MS: {
    left: [
      {
        headline:
          "In Mississippi's Senate race, GOP targets Democratic candidate's ad",
        outlet: "MS NOW",
        summary:
          "Mississippi Republicans' newfound objection to Scott Colom's AI ad showing Hyde-Smith fleeing reporters is the latest sign MAGA world is fretting over his Senate challenge.",
      },
      {
        headline:
          "A Mississippi Democrat is unexpectedly threatening Republicans' grip on the state",
        outlet: "The Guardian",
        summary:
          "Trump's late rally for Hyde-Smith is seen by many as an admission of GOP vulnerability, as Scott Colom says national Democrats long ignored Mississippi's loyal Black and white voters.",
      },
    ],
    right: [
      {
        headline: "Trump praises Cindy Hyde-Smith in Mississippi rally speech",
        outlet: "Fox News",
        summary:
          "At a Mississippi rally, President Trump championed Sen. Cindy Hyde-Smith and accused Democratic challenger Scott Colom of being a 'shameless phony' funded by George Soros.",
      },
      {
        headline: "Attorney General to Colom: Remove AI ads or face injunction",
        outlet: "Magnolia Tribune",
        summary:
          "Attorney General Lynn Fitch told Democrat Scott Colom his AI-generated ads of Sen. Hyde-Smith appear to violate Mississippi's deepfake law, warning of an injunction and possible criminal penalties.",
      },
    ],
    wire: [
      {
        headline:
          "Trump campaigns in Mississippi, highlighting Republicans' challenges",
        outlet: "Associated Press",
        summary:
          "Trump rallied in Corinth for Sen. Cindy Hyde-Smith, who faces a surprisingly stiff challenge from Democrat Scott Colom, saying 'I can't take a chance' as Colom talked with rallygoers outside.",
      },
      {
        headline:
          "Why former VP Harris said there is hope for Colom, MS on Election Day",
        outlet: "Clarion Ledger",
        summary:
          "Kamala Harris rallied for Scott Colom at his Columbus alma mater, where Colom attacked Hyde-Smith's record on tariffs and health care and Harris urged Mississippians to elect him.",
      },
    ],
  },
  MT: {
    left: [
      {
        headline:
          "Why I am resigning as Montana Democratic Party chair to endorse an independent Senate candidate",
        outlet: "MS NOW",
        summary:
          "Resigning chair Shannon O'Brien writes that Republican Kurt Alme would be a Trump 'puppet,' questions Democrat Alani Bankhead's commitment to abortion rights, and endorses independent Seth Bodnar.",
      },
      {
        headline:
          "The Rise of Independents: Will 2026 be a historic year for them?",
        outlet: "MS NOW",
        summary:
          "As red-state independents surge, Montana's Seth Bodnar tells Antonia Hylton how a renewed Iran war would hit Montanans and how he'd work with both parties.",
      },
    ],
    right: [
      {
        headline:
          "Montana Democratic Party chairwoman quits, backs independent in Senate race",
        outlet: "Washington Examiner",
        summary:
          "Montana Democratic chairwoman Shannon O'Brien resigned and endorsed independent Seth Bodnar over her own party's nominee, Alani Bankhead, saying Bankhead lacks 'the momentum, resources or will to win.'",
      },
      {
        headline:
          "Pete Buttigieg endorses independent in Montana Senate race over Democratic candidate",
        outlet: "Washington Examiner",
        summary:
          "Pete Buttigieg, rumored to be weighing a 2028 bid, endorsed independent Seth Bodnar, bypassing Montana's Democratic nominee less than two weeks before the election.",
      },
    ],
    wire: [
      {
        headline: "Senate hopefuls trade jabs at first all-candidate debate",
        outlet: "Montana Free Press",
        summary:
          "Republican Kurt Alme, in his first debate, drew most attacks over tariffs, immigration and the Iran war, while Bankhead and Libertarian Kyle Austin questioned whether Bodnar is truly independent.",
      },
      {
        headline: "Four candidates face-off in chaotic Senate race",
        outlet: "Montana Public Radio",
        summary:
          "Republican Kurt Alme runs a quiet campaign leaning on Trump-like rhetoric, while independent Seth Bodnar and Democrat Alani Bankhead compete for the same centrist and left-wing voters.",
      },
    ],
  },
  NC: {
    left: [
      {
        headline: "Roy Cooper talks AI, war, and healthcare in Greensboro",
        outlet: "Cardinal & Pine",
        summary:
          "On his 'Make Stuff Cost Less' tour, Cooper pledged to fight Medicaid cuts, restore health subsidies, end the costly Iran war Whatley defends, and put guardrails on AI.",
      },
      {
        headline:
          "Trump Makes Ugly New Threat About What He'll Do If His Candidate Loses",
        outlet: "HuffPost",
        summary:
          "Stumping for Michael Whatley in Gastonia, Trump drew groans by saying he'd withhold FEMA disaster money from hurricane-battered North Carolina if Whatley loses, before claiming he was only kidding.",
      },
    ],
    right: [
      {
        headline:
          "NSJ questioned Cooper's campaign, got cease-and-desist in response",
        outlet: "The North State Journal",
        summary:
          "When the paper asked whether Roy Cooper, 69, would serve a full six-year term and had health problems, Elias Law Group answered with a cease-and-desist threatening a defamation suit.",
      },
      {
        headline: "Trump rallies in Greenville for Whatley, Buckhout, Balkcom",
        outlet: "Carolina Journal",
        summary:
          "Two weeks before Election Day, Trump said Roy Cooper 'betrayed' North Carolina for 38 years, and Whatley vowed to cut taxes, calling Cooper a tax-raiser who vetoed six tax cuts.",
      },
    ],
    wire: [
      {
        headline:
          "Trump and Whatley double down on the administration's record in North Carolina Senate contest",
        outlet: "Associated Press",
        summary:
          "In Gastonia, Trump and his handpicked nominee Michael Whatley embraced and offered withering attacks on Roy Cooper, despite voter frustration over the Iran war and the economy.",
      },
      {
        headline:
          "Cooper, Whatley react as Trump raises questions about Camp Lejeune settlements",
        outlet: "The News & Observer",
        summary:
          "After Trump said Camp Lejeune victims won't get paid unless Republicans win, Cooper called it 'disgusting' extortion of service members; Whatley said 'we have to take care of the families.'",
      },
    ],
  },
  NE: {
    left: [
      {
        headline:
          "'Prairie populists' fight to win back rural voters long ceded to Republicans",
        outlet: "The Guardian",
        summary:
          "Touring all 93 Nebraska counties, independent Dan Osborn pitches anti-corruption 'prairie populism' to rural voters despairing over gas prices and Trump's agenda, part of a Midwest-wide populist push.",
      },
      {
        headline: "Jim Pillen Wants To Raise Your Taxes",
        outlet: "Daily Kos",
        summary:
          "Gov. Jim Pillen blew a $1 billion surplus on tax cuts for the wealthy and would revive his sales-tax hike if reelected, this community post argues, backing Lynne Walz.",
      },
    ],
    right: [
      {
        headline:
          "Senate hopeful's 'treading water' narrative crumbles under $500K campaign payouts to family",
        outlet: "Fox News",
        summary:
          "Independent Dan Osborn says his family is 'treading water,' yet records show his campaign paid relatives over $500,000; a watchdog says he's co-opting Nebraskans' real financial pain.",
      },
      {
        headline:
          "Pillen Receives Endorsement From Three Nebraska Police Unions, Praised for Juvenile Crime Legislation",
        outlet: "The Plains Sentinel",
        summary:
          "Omaha, Lincoln and statewide police unions endorsed Gov. Jim Pillen, crediting his juvenile justice law with all-time-low car thefts and shootings as he touts Nebraska as America's safest state.",
      },
    ],
    wire: [
      {
        headline:
          "Trump signs order extending tax-free use of red-dyed diesel during midterm stop in Nebraska",
        outlet: "Associated Press",
        summary:
          "Campaigning in Grand Island for Sen. Pete Ricketts, facing an independent backed by national Democrats, Trump signed an order easing tax-free dyed diesel use, saying it would save farmers millions.",
      },
      {
        headline:
          "Walz highlights Republican endorsements as NRA makes Nebraska stop to endorse Pillen",
        outlet: "Nebraska Public Media",
        summary:
          "Democrat Lynne Walz rolled out endorsements from three former Republican state senators criticizing Gov. Jim Pillen's budget record, while the NRA traveled to Omaha to endorse Pillen.",
      },
    ],
  },
  NH: {
    left: [
      {
        headline: "In New Hampshire's Senate race, Pappas takes on Sununu",
        outlet: "Slate",
        summary:
          "A New Hampshire-based Slate writer argues 'boring' Chris Pappas is earnestly running to legislate, while Trump-endorsed ex-lobbyist John Sununu runs a low-key race leaning on his family name.",
      },
      {
        headline:
          "Ayotte won't say whether she still owns data center stock now worth nearly 1M",
        outlet: "Granite Post",
        summary:
          "After clearing the way for data centers, Gov. Kelly Ayotte flipped to backing a moratorium but won't say if she still holds Bloom Energy stock now worth nearly $1 million.",
      },
    ],
    right: [
      {
        headline:
          "New Hampshire's Sununu says he's 'never even met' Trump amid attacks from Democratic opponent",
        outlet: "Washington Examiner",
        summary:
          "John E. Sununu's new ad strikes an independent tone, saying he's never met Trump and opposed the Iran war, while tying Chris Pappas to Nancy Pelosi and 'California.'",
      },
      {
        headline:
          "On Energy, Ayotte's 'All of the Above,' Warmington's Still AWOL",
        outlet: "New Hampshire Journal",
        summary:
          "Gov. Kelly Ayotte pushes 'all of the above' energy and nuclear power to cut bills, while Cinde Warmington still hasn't released her promised energy plan and opposes natural gas expansion.",
      },
    ],
    wire: [
      {
        headline: "Sununu and Pappas clash in final debate of U.S. Senate race",
        outlet: "New Hampshire Public Radio",
        summary:
          "In their final WMUR debate, Chris Pappas cast John Sununu as beholden to Trump and Sununu called Pappas an ineffective career politician; they sparred over Iran, costs and Social Security.",
      },
      {
        headline:
          "Ayotte, Warmington debate affordability, school funding at Manchester forum",
        outlet: "New Hampshire Public Radio",
        summary:
          "At a Manchester forum, Gov. Kelly Ayotte defended New Hampshire's low-tax model and housing efforts, while Cinde Warmington said the economy is 'fundamentally broken'; both backed a data center moratorium.",
      },
    ],
  },
  NJ: {
    left: [
      {
        headline:
          "Cory Booker, Justin Murphy split on Iran war, economy during US Senate debate",
        outlet: "New Jersey Monitor",
        summary:
          "Justin Murphy backed Trump's Iran war and touted a 'manufacturing resurgence' that federal data undercut, while Cory Booker said the war and tariffs cost families $4,000 to $5,000.",
      },
      {
        headline: "Candidates make final push as Election Day 2026 nears",
        outlet: "New Jersey Monitor",
        summary:
          "Seeking a third term, Cory Booker told a Flemington rally 'we are in crisis' as the American dream slips away, urging Democrats to retake Congress through New Jersey.",
      },
    ],
    right: [
      {
        headline:
          "NJ attorney goes viral after Iran debate challenge to Cory Booker",
        outlet: "Fox News",
        summary:
          "Justin Murphy says his unscripted style, shown in a viral 'finish it' Iran answer, could end New Jersey's half-century GOP Senate drought, arguing 13-year senator Cory Booker can't blame Trump.",
      },
    ],
    wire: [
      {
        headline:
          "'Get your cowboy hat on.' Booker's odd, cordial, Trump-focused N.J. Senate debate.",
        outlet: "NJ.com",
        summary:
          "In a cordial, Trump-focused debate, Cory Booker called the Iran war 'colossally stupid' while Justin Murphy urged Trump to 'get your cowboy hat on' and finish it.",
      },
      {
        headline:
          "New Jersey election 2026: Cory Booker faces 3 challengers in U.S. Senate race. Here's what to know",
        outlet: "WHYY",
        summary:
          "Incumbent Cory Booker, touting his Keep Your Pay Act and opposing Trump's tariffs and Iran war, faces Republican Justin Murphy, who wants to abolish the IRS for a flat tax.",
      },
    ],
  },
  NM: {
    left: [
      {
        headline:
          "National Dems get a taste of New Mexico politics at Luján's election season matanza",
        outlet: "New Mexico Political Report",
        summary:
          "Sen. Ben Ray Luján hosted Sens. Chris Van Hollen and Mark Kelly at a matanza celebrating New Mexico's early 2028 primary slot, doubling as a get-out-the-vote push before Nov. 3.",
      },
      {
        headline:
          "Warren stumps for Haaland in ABQ as both pitch New Mexico as affordability solution for Dems",
        outlet: "New Mexico Political Report",
        summary:
          "Elizabeth Warren joined Deb Haaland at an Albuquerque childcare center touting New Mexico's free universal childcare as a national affordability model Haaland would expand; Hull favors co-pays for higher earners.",
      },
    ],
    right: [
      {
        headline: "NM Senate Head to Head: Luján's Record vs. Marker's Case",
        outlet: "New Mexico Madness",
        summary:
          "Nearly 18 years of Luján's seniority haven't lifted New Mexico off the bottom on child well-being, education and crime; Marker offers tougher border policy and all-of-the-above energy.",
      },
      {
        headline: "One candidate, two podiums: Hull goes solo on KOB",
        outlet: "Piñon Post",
        summary:
          "With Democrat Deb Haaland a no-show at KOB's debate, Republican Gregg Hull faced an empty podium and delivered an unrebutted, policy-heavy pitch on affordability, crime, child welfare and data centers.",
      },
    ],
    wire: [
      {
        headline:
          'Election 2026: Larry "Lead" Marker wants to bring a conservative view to NM\'s U.S. Senate representation',
        outlet: "KUNM Public Radio",
        summary:
          "Write-in primary winner Larry 'Lead' Marker told KUNM he'd bring conservative representation, pitching a windfall sovereign wealth fund for Social Security and faulting Luján for refusing to debate.",
      },
      {
        headline:
          "In New Mexico governor's race, Deb Haaland's historic bid tests Democrats' political hold",
        outlet: "Associated Press",
        summary:
          "If elected, Haaland would be the first Native American woman to lead a state, but she must convince disenchanted voters to keep Democrats in charge; Hull promises tax cuts.",
      },
    ],
  },
  NV: {
    left: [
      {
        headline:
          "Aaron Ford would have 'unequivocally' denied clemency for pregnant woman's killer",
        outlet: "The Nevadan",
        summary:
          "Aaron Ford's campaign says GOP attacks are misleading: his sponsorship concerned an earlier robbery sentence, he'd have voted no, and Lombardo, on the pardons board, could have pulled the application.",
      },
      {
        headline:
          "Lombardo touts tough-on-crime, but his record paints a different story",
        outlet: "The Nevadan",
        summary:
          "As pro-Lombardo ads credit his crime bill with a 16% drop, The Nevadan finds Clark County violent crime fell before it took effect and trooper vacancies near 50% worsened.",
      },
    ],
    right: [
      {
        headline:
          "'Thrill killer' and desert executioner — meet the dozens of criminals Dem AG backed for parole: 'No brainer'",
        outlet: "Fox News",
        summary:
          "Fox News Digital found Democratic Attorney General Aaron Ford voted to pardon or significantly cut sentences for 30 convicted murderers, sometimes breaking with Gov. Joe Lombardo on violent cases.",
      },
      {
        headline:
          "Why Nevada's Top Democratic Prosecutor Won't Back Aaron Ford for Governor",
        outlet: "Nevada News and Views",
        summary:
          "Democratic Clark County District Attorney Steve Wolfson endorsed Gov. Joe Lombardo; Chuck Muth says Aaron Ford's death-penalty opposition and clemency push for a killer explain why.",
      },
    ],
    wire: [
      {
        headline:
          "AI data centers are coming for federal land. They're roiling the Nevada governor's race",
        outlet: "Associated Press",
        summary:
          "Data centers on federal land have become a dominant issue, with Gov. Joe Lombardo open to them given community support and Aaron Ford vowing opposition, saying housing should come first.",
      },
      {
        headline:
          "On the issues: Can the economy tip the Nevada governor's race?",
        outlet: "Las Vegas Review-Journal",
        summary:
          "Aaron Ford says Gov. Joe Lombardo and Trump are raising costs amid high unemployment; Lombardo touts 100,000 new jobs, falling unemployment, no new taxes and a sales tax holiday.",
      },
    ],
  },
  NY: {
    left: [
      {
        headline: "5 takeaways from New York's only governor's debate",
        outlet: "Courier New York",
        summary:
          "Hochul kept Trump at the center of the only debate, Blakeman backpedaled on praising Trump and declined to condemn an ICE shooting, and his Mamdani attacks surfaced only late.",
      },
      {
        headline:
          "'Well...': Kathy Hochul Hits Back After MAGA Darling Nicki Minaj Calls Her An 'Evil Woman'",
        outlet: "HuffPost",
        summary:
          "A stunned Hochul told Trump-backing rapper Nicki Minaj, who endorsed Blakeman and called her 'evil,' to 'get offline' and stop attending Trump rallies because they're 'poisoning your mind.'",
      },
    ],
    right: [
      {
        headline:
          "Bruce Blakeman calls Kathy Hochul Zohran Mamdani's 'puppet' during New York campaign rally",
        outlet: "Fox News",
        summary:
          "Blakeman blasted Hochul as Mayor Zohran Mamdani's 'puppet' at a Sunday rally, accusing her of embracing the democratic socialist's agenda while failing to crack down on illegal immigration and crime.",
      },
      {
        headline:
          "Blakeman backed by police unions from across NYS as gov race enters final stretch: 'Common sense'",
        outlet: "New York Post",
        summary:
          "Police union leaders from Nassau to Buffalo endorsed Blakeman as the 'common sense' choice to make New York safe, while Blakeman said Hochul's policies make it easier for criminals.",
      },
    ],
    wire: [
      {
        headline:
          "Trump veers into Nobel Prize grievances during upstate New York rally on day award is announced",
        outlet: "Associated Press",
        summary:
          "At a Syracuse rally meant to boost Republican Bruce Blakeman, Trump spent long stretches attacking the Nobel committee and Attorney General Letitia James while criticizing Gov. Kathy Hochul.",
      },
      {
        headline:
          "Why Mamdani's name was mostly missing from debate for New York governor",
        outlet: "Gothamist (WNYC)",
        summary:
          "Trump came up over 50 times in the only debate, while Blakeman waited 40-plus minutes to mention Mayor Zohran Mamdani, then called him antisemitic; Hochul called Mamdani 'a partner.'",
      },
    ],
  },
  OH: {
    left: [
      {
        headline: "A data center backlash is upending Ohio's Senate race",
        outlet: "MS NOW",
        summary:
          "Brown is betting bipartisan anger at Ohio's data center boom will help him unseat Jon Husted, whom ads brand 'the face of data centers'; Brown calls it a rigged system.",
      },
      {
        headline:
          "Trump attacks Ohio governor over call for Ramaswamy to drop 'deeply troubling' ad",
        outlet: "The Guardian",
        summary:
          "Republican Gov. Mike DeWine called Ramaswamy's ad featuring Amy Acton's estranged mother 'wrong' and urged its removal; Trump then branded DeWine disloyal and told Ramaswamy to keep airing it.",
      },
    ],
    right: [
      {
        headline:
          "Republicans in historically critical battleground county pull no punches in midterm homestretch: 'Lot of hope'",
        outlet: "Fox News",
        summary:
          "At a Butler County GOP event, Sen. Jon Husted said no one can name anything Sherrod Brown did in 50 years and blamed Democrats for blocking his data center bill.",
      },
      {
        headline:
          "Vivek Ramaswamy defends controversial ad featuring Amy Acton's estranged mom – admits his campaign is behind it",
        outlet: "New York Post",
        summary:
          "Ramaswamy owned the ad in which Acton's estranged mother calls her a 'liar,' saying Acton has been 'hiding' from issues, refuses to debate and has spread lies about his family.",
      },
    ],
    wire: [
      {
        headline:
          "2026 race for U.S. Senate in Ohio: the candidates' thoughts on data centers, war in Iran, immigration",
        outlet: "The Statehouse News Bureau (Ohio public media)",
        summary:
          "Brown's ads call Husted 'the face of data centers'; Husted calls Brown a 'chameleon' on it and ties him to Biden-era open borders; they split on ending the Iran war.",
      },
      {
        headline:
          "Ramaswamy takes responsibility for ad with Amy Acton's mother and will spend millions to promote it",
        outlet: "Associated Press",
        summary:
          "Ramaswamy's campaign confirmed it made the ad featuring Amy Acton's estranged mother and will spend millions airing it despite bipartisan rebuke for dredging up the Democrat's painful childhood.",
      },
    ],
  },
  OK: {
    left: [
      {
        headline: "What's At Stake Nov. 3 In Oklahoma",
        outlet: "The Oklahoma Observer",
        summary:
          "Former Senate colleague Cal Hobson calls Mazzei a flip-flopper on the foreign-owned Inola smelter and urges rural voters to back Munson's public-school investment over private-school subsidies.",
      },
    ],
    right: [
      {
        headline:
          "'Get you're a**es in that car. We're going to Oklahoma': Trump's motorcade speeds to Okla. after weather prevents flights ahead of campaign rally",
        outlet: "One America News Network",
        summary:
          "Racing to Durant by motorcade, Trump urged Oklahomans to send Kevin Hern to the Senate, warning socialist N'Kiyla Thomas would 'eliminate capitalism' and 'destroy the Oklahoma way of life.'",
      },
      {
        headline:
          "In Norman, Munson Said Stop Public Dollars for Private Schools - and Pause New AI Data Centers",
        outlet: "46th State",
        summary:
          "At a Norman coffee shop, Democrat Cyndi Munson vowed to stop 'putting public dollars in private schools' and pause new AI data centers, a rollback school-choice voters should note.",
      },
    ],
    wire: [
      {
        headline:
          "Democratic socialist Thomas challenges Hern for Oklahoma U.S. Senate seat",
        outlet: "The Oklahoman",
        summary:
          "Trump loyalist Rep. Kevin Hern, running on border security and ending abortion, faces nurse N'Kiyla Jasmine Thomas, a democratic socialist championing Medicare for all and universal child care.",
      },
      {
        headline:
          "Mike Mazzei, Cyndi Munson face off in gubernatorial debate ahead of midterm election",
        outlet: "KGOU (Oklahoma's NPR Source)",
        summary:
          "In a pre-taped OETA debate, Mazzei pitched tax cuts including ending the income tax, while Munson cast herself as a governor for all Oklahomans regardless of party.",
      },
    ],
  },
  OR: {
    left: [
      {
        headline: "WW's Fall 2026 Endorsements: Federal",
        outlet: "Willamette Week",
        summary:
          "Endorsing Jeff Merkley over Trump supporter David Brock Smith, the paper says Democrats are poised to bring 'a reckoning' for a president who has held himself above the law.",
      },
      {
        headline: "Mercury Endorsements: Vote Kotek For Governor",
        outlet: "Portland Mercury",
        summary:
          "Despite qualms about Kotek's business coziness, the Mercury backs her over Christine Drazan, whom it accuses of climate-denialist, anti-abortion and transphobic talking points and calls 'out of step with reality.'",
      },
    ],
    right: [
      {
        headline:
          "Smith Challenges Merkley to Defend His Record as Merkley Refuses to Debate",
        outlet: "Right Now Oregon",
        summary:
          "With Merkley refusing to debate, Republican David Brock Smith appeared alone at KTVZ's town hall and made the incumbent's absence an accountability issue: 'I have chosen to show up.'",
      },
      {
        headline: "The Oregonian endorses four more years of failure",
        outlet: "Washington Examiner",
        summary:
          "The Oregonian's 'dispirited' endorsement of Gov. Tina Kotek over Christine Drazan offers a window into the failing liberal worldview behind Democratic states' 'death spiral of poor governance.'",
      },
    ],
    wire: [
      {
        headline:
          "U.S. Sen. Jeff Merkley was headed for retirement. President Trump's actions changed his mind",
        outlet: "Oregon Public Broadcasting",
        summary:
          "Merkley says Trump's courting of billionaires, loyalist appointments and National Guard federalization talked him out of retiring, while Republican David Brock Smith campaigns on high taxes and living costs.",
      },
      {
        headline:
          "We asked Kotek, Drazan what they'd change first if elected: The latest on Oregon's 2026 election",
        outlet: "The Oregonian",
        summary:
          "Kotek named bills regulating data centers, spinning off the State Hospital and creating a Commerce Department; Drazan pledged a day-one homelessness emergency and a full audit of homelessness spending.",
      },
    ],
  },
  PA: {
    left: [
      {
        headline:
          "10 election deniers are on Pennsylvania's ballot this November",
        outlet: "The Keystone",
        summary:
          "Republican Stacy Garrity, who called 2020 'tarnished forever' and told a Trump rally 'we know that he won,' is among 10 election deniers on Pennsylvania's ballot.",
      },
      {
        headline:
          "Midterm election 2026: How Shapiro and Garrity differ on the economy",
        outlet: "The Pennsylvania Independent",
        summary:
          "Shapiro touts job growth and suing to end Trump's tariffs, while longtime Trump ally Garrity, who once called the tariffs 'fantastic,' has since distanced herself from them.",
      },
    ],
    right: [
      {
        headline:
          "Josh Shapiro refuses to commit to finishing term as Pa. governor — similar to another 2028 hopeful",
        outlet: "New York Post",
        summary:
          "At his only debate with Stacy Garrity, Shapiro wouldn't promise to serve a full second term as 2028 rumors swirl, instead attacking Garrity for breaking her pledge as treasurer.",
      },
      {
        headline:
          "Exclusive: Garrity unveils new 'Twilight Justice' political ad",
        outlet: "Broad + Liberty",
        summary:
          "Garrity's new web ad features a young sleuth filing Right-to-Know requests to probe Shapiro administration 'scandals,' from a $295,000 sexual-harassment payout to who rides on state planes.",
      },
    ],
    wire: [
      {
        headline:
          "Tariffs, taxes and other takeaways from Josh Shapiro and Stacy Garrity's governor debate",
        outlet: "WHYY",
        summary:
          "Garrity accused Shapiro of driving the economy into the ground while Shapiro painted her as allegiant to Trump, clashing over voter ID and certifying the 2028 election.",
      },
      {
        headline:
          "Some Pennsylvania Republicans are aligning themselves to Shapiro. What does that mean for the midterms?",
        outlet: "The Philadelphia Inquirer",
        summary:
          "Several Republicans in tight races are featuring Democratic Gov. Josh Shapiro in their ads, leaving GOP nominee Stacy Garrity out even as she campaigns alongside them.",
      },
    ],
  },
  RI: {
    left: [
      {
        headline: "Governor Debate Recap: Housing, Power Bills and Taxes",
        outlet: "Uprise RI",
        summary:
          "Foulkes, Block and Guckian split on housing and electric bills, several answers lacking costs, while conservative moderator Gene Valicenti spent airtime on transgender athletes, a vanishingly rare situation.",
      },
    ],
    right: [
      {
        headline: "A question of judgment",
        outlet: "The Valley Breeze",
        summary:
          "Columnist Aidan Carey says Helena Foulkes's pledge to vote for 'socialist' David Morales shows her vaunted judgment is just one-party orthodoxy, urging voters to choose something different for governor.",
      },
    ],
    wire: [
      {
        headline: "RI governor's race hijacks the airwaves with attack ads",
        outlet: "The Providence Journal",
        summary:
          "Foulkes is running ads tying former Republican Ken Block to Trump and calling both 'horrible,' while Block's ad brands her a union 'puppet' linked to the Washington Bridge debacle.",
      },
      {
        headline: "Block, Foulkes and Guckian trade jabs in final debate",
        outlet: "Ocean State Media",
        summary:
          "Guckian branded Block a spoiler and vowed a 10% budget cut, Block called Foulkes 'a new face in front of the same political machine,' and Foulkes defended her CVS record.",
      },
    ],
  },
  SC: {
    left: [
      {
        headline:
          "Ex-South Carolina governors endorse Democrat Annie Andrews in Senate race",
        outlet: "The Guardian",
        summary:
          "Republican David Beasley and Democrat Jim Hodges jointly endorsed Andrews against Trump-endorsed Darline Graham, with Beasley lamenting a Trump-era GOP that is 'not the party that I knew.'",
      },
      {
        headline: "Our view: If you want change, get out and vote",
        outlet: "Charleston City Paper",
        summary:
          "Urging change-minded voters to turn out, the paper backs Jermaine Johnson and Sam Skardon to lift South Carolina off 'the bottom of all of the bad lists' Republicans left untouched.",
      },
    ],
    right: [
      {
        headline:
          "Many of Annie Andrews's supposed Republican backers have history of supporting Democrats",
        outlet: "Washington Examiner",
        summary:
          "Andrews touts 101 'Republicans for Dr. Annie,' but an Examiner review found nearly a third donated only to Democrats and most have pulled Democratic primary ballots.",
      },
      {
        headline: "Crossroads 2026: South Carolina Governor's Debate",
        outlet: "FITSNews",
        summary:
          "At the only debate, 'traditional conservative' Wilson hammered affordability while Johnson, a self-styled 'new kind of Democrat' with old-school liberal views, decried 24 years of GOP 'broken promises.'",
      },
    ],
    wire: [
      {
        headline:
          "Trump brings a last-minute rally boost to Darline Graham in South Carolina's US Senate race",
        outlet: "Associated Press",
        summary:
          "Trump rallied in Pendleton for Graham, whose campaign stumbles have Democrats hopeful, calling Andrews 'one of the most crazy people' before drifting off-topic in a speech interrupted by protesters.",
      },
      {
        headline:
          "Contrasting SC governor's race: Alan Wilson's conservative leadership vs Jermaine Johnson's 'new SC'",
        outlet: "The Post and Courier",
        summary:
          "Wilson pairs school choice, tough-on-crime policies and phased income-tax elimination; Johnson opposes private-school vouchers and backs a data center moratorium, Medicaid expansion and taxing legal cannabis to fund relief.",
      },
    ],
  },
  SD: {
    left: [
      {
        headline:
          "South Dakota Is Trump Country. Can This New Strategy Change That?",
        outlet: "HuffPost",
        summary:
          "A lifelong independent who ran as a Democrat in 2022, Bengs bets his anti-corruption populism can crack deep-red South Dakota as Trump's tariffs and the Iran war squeeze farmers.",
      },
      {
        headline:
          "Farmer Pans Rhoden Weight-Limit Lift for Farm Trucks as Irresponsible Election Ploy",
        outlet: "Dakota Free Press",
        summary:
          "The blog and an Orland farmer blast Gov. Rhoden's order letting harvest trucks run 10% overweight as a cheap reelection ploy that wrecks roads while farmers pay 'Trump-flated' diesel prices.",
      },
    ],
    right: [
      {
        headline:
          "Brian Bengs struck mute when asked what Senate votes he disagreed with Democrats on. Also, what's with his breaking laws on disclaimers?",
        outlet: "South Dakota War College",
        summary:
          "Mocking the 'Dem/Independent/Whatever' candidate as a 'Fake Indy,' the blog says Bengs couldn't name a Senate vote where he'd break with Democrats and that his yard signs lack required disclaimers.",
      },
      {
        headline: "Rhoden for Governor Unveils “Honor” Ad",
        outlet: "South Dakota War College",
        summary:
          "Rhoden's closing ad, filmed in his welding shop, touts reduced crime, protecting life, strengthening the Second Amendment and 'the largest property tax cut in South Dakota history.'",
      },
    ],
    wire: [
      {
        headline:
          "Brian Bengs lands 2 major endorsements by independent initiatives before midterms",
        outlet: "Argus Leader",
        summary:
          "Bengs, a self-described 'Teddy Roosevelt Republican,' won endorsements from the Forward Party and the Independent Center, whose Adam Brandon called voting independent 'the most conservative thing you could do.'",
      },
      {
        headline:
          "Rhoden, Ahlers discuss affordability, water infrastructure and other issues at SDPB/SD News Watch debate",
        outlet: "South Dakota Public Broadcasting",
        summary:
          "In their Sioux Falls debate, Rhoden touted property tax cuts averaging 34% while Ahlers pitched childcare rebates and education funding; both said the state must do more on water infrastructure.",
      },
    ],
  },
  TN: {
    left: [
      {
        headline: "Let's Talk about Marquita Bradshaw – Tennessee",
        outlet: "Daily Kos",
        summary:
          "A Daily Kos community diarist champions Bradshaw, a 10th-generation Tennessean and environmental-justice activist, whose campaign centers on health care affordability, lower prescription costs and mental health services.",
      },
      {
        headline: "'Quit Calling Her Marsha!' (What would Abby do?)",
        outlet: "Daily Kos",
        summary:
          "A Daily Kos diarist casts Jerri Green, 'One Tough Mother,' as the remedy for Tennessee's closed hospitals and underfunded schools, mocking Blackburn for ducking debates and branding Green a 'socialist.'",
      },
    ],
    right: [
      {
        headline:
          "President Trump rallies Tennessee crowd ahead of midterms, floats 'Trump Ocean'",
        outlet: "Fox News",
        summary:
          "Campaigning in Clarksville for Sen. Bill Hagerty and Marsha Blackburn, Trump touted manufacturing investments and the Memphis crime crackdown and urged supporters to 'just pretend' he's running.",
      },
      {
        headline:
          "Blackburn Says Jerri Green 'Would Put Illegal Aliens over Your Family' After Democrat Votes for ICE to be Removed from Memphis",
        outlet: "The Tennessee Star",
        summary:
          "After Green voted to pull ICE and the National Guard from the Memphis Safe Task Force and likened ICE tactics to 'Nazi Germany,' Blackburn branded her a 'radical liberal socialist.'",
      },
    ],
    wire: [
      {
        headline:
          "GOP Sen. Bill Hagerty faces Memphis Democrat Marquita Bradshaw in rematch",
        outlet: "WPLN News (Nashville Public Radio)",
        summary:
          "In a 2020 rematch, environmental activist Bradshaw campaigns on teacher pay, affordable housing and health care, while Trump ally Hagerty backs mass deportations and pushes housing red-tape cuts.",
      },
    ],
  },
  TX: {
    left: [
      {
        headline: "MAGA's anti-Talarico smear blows up in their faces",
        outlet: "Daily Kos",
        summary:
          "MAGA media figures, amplified by Elon Musk, pushed a fake story that Talarico was treated at an Ohio mental health clinic, but the clinic denied it and the 'smear' collapsed.",
      },
      {
        headline:
          "Republicans suddenly aren't interested in debating their opponents",
        outlet: "MS NOW",
        summary:
          "Abbott, who debated all three past opponents, now refuses to face Hinojosa, part of a 'cowardly' GOP pattern of ducking scrutiny that includes Paxton's refusal to debate Talarico.",
      },
    ],
    right: [
      {
        headline:
          "Conspiracies mount after Texas Senate Democratic candidate James Talarico's absence for nine days due to 'nasty case of the flu'",
        outlet: "New York Post",
        summary:
          "Talarico's campaign clarified the timeline of his 'nasty' flu after a nine-day absence and 'discrepancies' fueled speculation he was in an Ohio mental health facility, a claim the clinic denied.",
      },
      {
        headline:
          "Hinojosa's Pro-Police Pitch Draws Scrutiny Over Defunding Record",
        outlet: "Texas Scorecard",
        summary:
          "Hinojosa's new pro-police ad clashes with her record: she defended Austin's $150 million police cuts in 2020, opposed a bill penalizing defunding cities and co-authored a bill ending qualified immunity.",
      },
    ],
    wire: [
      {
        headline:
          "Talarico vows to 'take back Texas' as Trump makes last-minute push to keep the state in GOP hands",
        outlet: "Associated Press",
        summary:
          "In Lubbock, Talarico decried a 'corruption crisis' and vowed to 'beat the billionaires,' while Trump, on his third Texas trip in a month, cast Talarico as too liberal for Texas.",
      },
      {
        headline: "Why Greg Abbott says he will not debate Gina Hinojosa",
        outlet: "Houston Chronicle",
        summary:
          "Abbott defended skipping a debate for the first time in his tenure, saying voters want their governor 'in their community,' as Hinojosa staged a 'debate' beside an empty lectern.",
      },
    ],
  },
  VA: {
    left: [
      {
        headline:
          "Video: Far-Right-Extremist 2026 VA GOP U.S. Senate Nominee, Bert Mizusawa, Yet AGAIN Defends a Third Trump Term in Bizarre CNN Interview",
        outlet: "Blue Virginia",
        summary:
          "Mizusawa told CNN's Jake Tapper a president can serve more than two terms, just not be elected to a third; the blog accuses mainstream media of 'sanewashing' him.",
      },
      {
        headline:
          "Senate candidate Bert Mizusawa backed Trump policies that raised costs for Virginians",
        outlet: "The Virginia Independent",
        summary:
          "Mizusawa campaigns on lowering costs but has defended tariffs and the Iran war that drove up prices, while Warner voted to pull out of Iran and end Canada tariffs.",
      },
    ],
    right: [
      {
        headline:
          "Who is Bert Mizusawa? The retired Major General challenging Sen. Mark Warner",
        outlet: "Washington Examiner",
        summary:
          "Mizusawa pitches himself as a battle-tested alternative whose four decades in uniform fit Virginia's military roots, while Warner, seeking a fourth term, has refused to debate.",
      },
      {
        headline: "Why is Sen. Mark Warner dodging debates?",
        outlet: "The Washington Times",
        summary:
          "The editorial board says Warner has caused two debates to be canceled with a weak Senate-schedule excuse to avoid defending his far-left record, and voters shouldn't reward him.",
      },
    ],
    wire: [
      {
        headline: "Former Republican Senate leader endorses Sen. Mark Warner",
        outlet: "Richmond Times-Dispatch",
        summary:
          "Former state Senate GOP leader Tommy Norment joined four other former Republican officials, including Barbara Comstock and Denver Riggleman, in endorsing Warner's bid for a fourth and final term.",
      },
      {
        headline:
          "Breaking down U.S. Senate race: Mizusawa hoping to unseat Warner, who's seeking his last term",
        outlet: "WVTF Radio IQ",
        summary:
          "Warner touts universal health care and making tech firms pay for AI job losses; Mizusawa wants spending cuts, a commodity-backed currency, and says Trump legally 'could' serve a third term.",
      },
    ],
  },
  VT: {
    left: [
      {
        headline:
          "A door to single-payer healthcare could open after November 3",
        outlet: "Vox",
        summary:
          "If elected, progressive newcomer Amanda Janoo would have Vermont pay the full tab for primary and preventive care, a pioneering experiment she hopes leads to single-payer.",
      },
      {
        headline:
          "The First Two Debates Gave a Boost to Amanda Janoo, But Not a Decisive One",
        outlet: "The Vermont Political Observer",
        summary:
          "Janoo came across as a credible, competent alternative while Scott, flustered in the WPTZ debate, avoided calamity; she declined to attack his record despite openings.",
      },
    ],
    right: [
      {
        headline:
          "Janoo claims her anti-F-35 statements taken 'out of context'",
        outlet: "Vermont Daily Chronicle",
        summary:
          "Democrat Amanda Janoo insisted her pledge to get F-35s 'out of our skies' was taken out of context; the Chronicle reprints her full Substack comment and lets readers judge.",
      },
      {
        headline:
          "Scott campaign challenges Janoo's universal primary care plan over costs, funding",
        outlet: "Vermont Daily Chronicle",
        summary:
          "Gov. Phil Scott's campaign says Amanda Janoo's free primary care promise could cost hundreds of millions more than her proposed taxes on high earners would raise.",
      },
    ],
    wire: [
      {
        headline:
          "Scott and Janoo differ on healthcare, school consolidation and taxes in first debate",
        outlet: "Vermont Public",
        summary:
          "In a remarkably friendly first debate, Republican Gov. Phil Scott and Democratic/Progressive challenger Amanda Janoo drew sharp contrasts on taxes, education and Janoo's plan for universal primary care.",
      },
      {
        headline:
          "Build or tax? Vermont's governor candidates emphasize different elements of housing policy",
        outlet: "Vermont Public",
        summary:
          "Gov. Phil Scott, who worked in construction, sees housing as a supply problem, while Amanda Janoo blames 'financialization' and would tax second homes and corporate owners more.",
      },
    ],
  },
  WI: {
    left: [
      {
        headline: "Governor debate: Crowley says Tiffany can't be trusted",
        outlet: "UpNorthNews",
        summary:
          "Crowley hammered Tiffany's record on schools, abortion rights, health care and elections, while Tiffany promised, without specifics, to fund services after freezing property taxes and giving away the surplus.",
      },
      {
        headline:
          "Commentary: Tom Tiffany blocks reporters, taking a page from Donald Trump",
        outlet: "Wisconsin Examiner",
        summary:
          "Despite softening his tone on Trump, Tiffany's campaign barred two Milwaukee Journal Sentinel reporters from an event and attacked one as a 'liberal activist,' showing the mask slipping.",
      },
    ],
    right: [
      {
        headline:
          "Abortion, ICE and socialism spark fireworks in swing state governor debate: 5 top moments",
        outlet: "Fox News",
        summary:
          "Tiffany pointed to Wisconsin's 20-week abortion limit while Crowley declined to name any gestational cutoff, and Tiffany vowed to return the entire $3 billion surplus through permanent tax cuts.",
      },
      {
        headline:
          "EXCLUSIVE: David Crowley's Milwaukee County Sent Almost $100 Million to His Wife Ericka's Employer, Audits Show",
        outlet: "Wisconsin Right Now",
        summary:
          "Audits show Crowley's Milwaukee County sent nearly $100 million to Community Advocates, the nonprofit that hired his wife Ericka a month after he took office, far more than before.",
      },
    ],
    wire: [
      {
        headline:
          "Wisconsin Republican Tiffany says Zuckerberg money was the reason he tried to overturn 2020 election",
        outlet: "Associated Press",
        summary:
          "At their only debate, Tiffany cited 'Zuckerbucks' grants for backing Trump's push to overturn Wisconsin's 2020 result; Crowley called that inadequate, and Tiffany pledged to certify 2028's winner.",
      },
      {
        headline:
          "Republicans scrutinize David Crowley's role in approving COVID-era grants to nonprofit his wife works for",
        outlet: "Wisconsin Public Radio",
        summary:
          "Republicans are targeting millions in pandemic rental aid that flowed to Community Advocates, where Crowley's wife works; his campaign calls it a 'new low,' noting he disclosed her job repeatedly.",
      },
    ],
  },
  WV: {
    left: [],
    right: [
      {
        headline:
          "Rachel Fetty Anderson Attends Martinsburg Candidate Forum as Capito Remains Absent",
        outlet: "Mountaineer Journal",
        summary:
          "Democrat Rachel Fetty Anderson answered voters' questions at a citizen-run Martinsburg forum while Sen. Shelley Moore Capito, though invited, stayed away; Anderson said public service means showing up.",
      },
      {
        headline:
          "Democrat U.S. Senate candidate raises alarm about A-I advancement",
        outlet: "West Virginia MetroNews",
        summary:
          "Rachel Fetty Anderson warned Trump forced AI into American life, urged shifting Pentagon money to universal health care, and said West Virginia's federal representatives shouldn't be reelected.",
      },
    ],
    wire: [
      {
        headline:
          "Election 2026: Eyeing third term, Capito faces Anderson, Wilson in U.S. Senate field",
        outlet: "Charleston Gazette-Mail",
        summary:
          "Trump ally Sen. Shelley Moore Capito, who backed the One Big Beautiful Bill Act's health and food-aid cuts, faces Democrat Rachel Fetty Anderson and Constitution Party nominee S. Marshall Wilson.",
      },
      {
        headline:
          "W.Va. candidates for U.S. Senate speak on Iran, tax cuts and more ahead of Senate election",
        outlet: "WVVA-TV",
        summary:
          "Capito touted her committee chairmanships and permitting reform to cut energy costs, while Fetty Anderson said Capito votes for donors and blamed the Iran war and tariffs for diesel prices.",
      },
    ],
  },
  WY: {
    left: [
      {
        headline:
          "CBC Roundup: James Byrd for US Senate (WY) – There is more strength when we work together",
        outlet: "Daily Kos",
        summary:
          "Daily Kos spotlights Democrat James Byrd, a fifth-generation Wyomingite and son of Cheyenne's first Black police chief and the state's first Black woman legislator, challenging Harriet Hageman.",
      },
      {
        headline: "Four takeaways from the Wyoming governor's debate",
        outlet: "WyoFile",
        summary:
          "Republican Eric Barlow kept things positive while Constitution Party candidate Rebecca Bextel, echoing the Freedom Caucus, stayed in attack mode and said she might veto everything for two years.",
      },
    ],
    right: [
      {
        headline:
          "Opponents Blast Hageman, Gray For Skipping PBS Debates Ahead Of General Election",
        outlet: "Cowboy State Daily",
        summary:
          "Hageman didn't answer Wyoming PBS's Senate debate invitation, drawing Democrat James Byrd's criticism; her campaign says she'll keep talking directly to voters through town halls in every county.",
      },
      {
        headline:
          "At Wyoming Gov. Debate, Bextel Bashes Barlow Almost The Whole Time",
        outlet: "Cowboy State Daily",
        summary:
          "Constitution Party nominee Rebecca Bextel called Republican Eric Barlow a 'swamp creature' and 'Democrat' throughout the debate, while Barlow leaned on experience and Democrat Ken Casner decried government obfuscation.",
      },
    ],
    wire: [
      {
        headline: "Senate candidates talk public lands, healthcare and energy",
        outlet: "Wyoming Public Media",
        summary:
          "Hageman stresses coal leasing and backs the Iran war, while Byrd wants energy revenue invested in new technologies, opposes selling public lands and says the war must stop.",
      },
      {
        headline:
          "Gordon endorses Eric Barlow for governor. Here's what comes next",
        outlet: "Casper Star-Tribune",
        summary:
          "Retiring Gov. Mark Gordon endorsed Republican nominee Eric Barlow, citing longtime ties; Barlow, facing Democrat Ken Casner and Constitution Party's Rebecca Bextel, is assembling a health care strike team.",
      },
    ],
  },
};
